import React, { useState, useEffect, useContext, useRef, forwardRef, useImperativeHandle } from "react";
import ReadOnlyExemptButton from "../../components/ReadOnlyExemptButton";
import { useTranslation } from "react-i18next";
import {
  Box, Button, ButtonGroup, Text,
  TableContainer, Table, Thead, Tbody, Tr, Th, Td, Flex,
  useDisclosure,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  FormControl,
  FormErrorMessage,
  Select,
  Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverCloseButton, PopoverBody, Portal,
  HStack,
} from "@chakra-ui/react";
import { FaRegQuestionCircle } from "react-icons/fa";
import Decimal from 'decimal.js';
import dayjs from "dayjs";

import type {
  ProjectWithRdsData,
  ProjectContextType, PagePermissions,
  ConfirmorInfo,
  Errors,
  PartNo,
  MonthValue,
  MonthlyExpenseData,
  EquipmentType,
} from '../../contexts/types';
import {
  getFiscalMonthsString,
  getTotalFiscalYearsByProject,
  calcTotalCycleTimeByMonth,
  calcTotalQuantityByMonth,
  calcCycleTimeBasedCost,
  applyBaseUpRateToLaborCost,
  getQuantitiesByPartNoMonth,
  calcEquipmentDepreciationByYearMonth,
  convertEquipmentData,
  generateYearMonthList,
} from '../../utils/calculation';

import '../common.css';
import { formatNumber, getLocalCurrencyId, getTaxRateFromSnapshot, hasDecimal } from '../../utils/utils';
import { parsePAndLFile } from '../../utils/profitAndLossLoad';
import { useToast } from "@chakra-ui/react";
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { getConfirmor, updateConfirm } from "../../utils/workflow";
import { CustomNumberInput } from "./CustomNumberInput";
import { WorkflowId, Messages, CurrencyNameJPY, EquipmentUsageType, isSimpleApplicationForm, isScanBlocked } from "../../contexts/constants";
import { useAppSelector } from "../../store/hooks";
import ConfirmorList from "./ConfirmorList";
import GoogleAPI from "../../utils/GoogleAPI";
import FileUploadModal from "../Modal/FileUploadModal";
import { fetchProcess } from "./ProcessFlow";

// コンテキスト
import { ProjectContext, getPermissions } from "./InvestmentScreeningDetail";
import HelpIcon from "./HelpIcon";

type ProfitAndLossProps = {
  projectId: string;
  /** 親から渡される初期目的（projectData取得前の判定用） */
  initialPurpose?: string;
  /** 親から渡される初期申請書様式（projectData取得前の判定用） */
  initialApplicationFormType?: string;
};

export interface ProfitAndLossHandle {
  getSnapshot: () => Record<string, unknown>;
  applySnapshot: (data: Record<string, unknown>) => void;
}

type UploadedFile = {
  key: string;
  fileName: string;
  lastModified: string;
  size: number;
  file: File;
  url: string | null;
  /** マルウェアスキャン状態（THREATS_FOUND の場合 Base64 は取得できない） */
  scanStatus?: string;
};

interface Confirmor {
  profitAndLoss?: {
    companyDivision?: string[];
    companyHeadDivision?: string[];
  };
}

// 精度を50桁に設定
Decimal.set({ precision: 50 });

/**
 * ProfitAndLoss コンポーネント
 */
const ProfitAndLoss = forwardRef<ProfitAndLossHandle, ProfitAndLossProps>(({ projectId, initialPurpose, initialApplicationFormType }, ref) => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const WebAPI = useContext(WebAPIContext);

  /** 画面タイトル */
  const TITLE = t('features.InvestmentScreening.ProfitAndLoss.title');

  /** Google Spread関連定数 */
  const PROFIT_AND_LOSS_FOLDER_PATH = "CEAS/SpreadSheet/ProfitAndLoss";
  const googleAPI = new GoogleAPI();

  // P&Lファイル 日本円通貨単位
  const JP_CURRENCY_UNIT = t('features.InvestmentScreening.ProfitAndLoss.currencyUnit.jpy');
  const JP_CURRENCY_UNIT_SCALE = 1000000;

  // P&Lファイル 現地通貨単位
  const LC_CURRENCY_UNIT = t('features.InvestmentScreening.ProfitAndLoss.currencyUnit.local');
  const LC_CURRENCY_UNIT_SCALE = 1000;

  /** 入力項目幅（英語版はラベル文言が長くなるため幅を拡張。日本語版は従来通り） */
  const WIDTH_RATE_VALUE = i18n.language === 'en' ? 300 : 180;

  const employeeId = useAppSelector((state) => state.employee.employeeId);
  const divisionId = useAppSelector((state) => state.employee.divisionId);

  // -----------------------------
  // useRef
  // -----------------------------
  const refs: Record<string, React.RefObject<HTMLButtonElement>> = {
    companyDivision: useRef<HTMLButtonElement>(null),
    companyHeadDivision: useRef<HTMLButtonElement>(null),
    salesDivision: useRef<HTMLButtonElement>(null),
    procurementDivision: useRef<HTMLButtonElement>(null),
  };

  // -----------------------------
  // useState
  // -----------------------------
  // Projectデータ
  const [projectData, setProjectData] = useState<ProjectWithRdsData>();

  // 簡易モード判定（projectData未取得時は親から渡された初期値で判定 → ちらつき防止）
  const isSimpleMode = isSimpleApplicationForm(
    projectData?.purpose ?? initialPurpose,
    projectData?.applicationFormType ?? initialApplicationFormType,
  );
  /** ローカル通貨ID（会社/工場スナップショットから取得） */
  const localCurrencyId = getLocalCurrencyId(projectData?.companyPlantSnapshot);
  // P&Lファイルデータ（複数ファイル対応）
  const [uploadedPLFileContents, setUploadedPLFileContents] = useState<{ fileName: string; content: string }[]>([]);
  // 選択通貨(ローカル)
  const [selectedCurrency, setSelectedCurrency] = useState<{ id: string; name: string; exchangeRate: number | null }>({
    id: "",
    name: "",
    exchangeRate: null,
  });
  // 選択中P&Lファイル名
  const [profitAndLossFileName, setProfitAndLossFileName] = useState<string | null>(null);
  // 年度集計データ（複数ファイル対応）
  const [fiscalDataList, setFiscalDataList] = useState<{ fileName: string; fiscalData: any; plTitle: string; currency: string }[]>([]);
  // P&Lファイル画面表示用データ（複数ファイル対応）
  const [dateRangeLabelList, setDateRangeLabelList] = useState<{ fileName: string; label: string }[]>([]);

  // 年月配列
  const [fiscalMonthsString, setFiscalMonthsString] = useState<string[]>([]);
  // 年月配列（年・月オブジェクト）
  const [fiscalMonthsLabelless, setFiscalMonthsLabelless] = useState<{ year: number; month: number }[]>([]);
  // その他の設備費
  const [otherEquipmentCost, setOtherEquipmentCost] = useState<MonthValue[]>([]);
  // 過去PJの投資対象設備費
  const [pastEquipmentCost, setPastEquipmentCost] = useState<MonthValue[]>([]);
  // 開発費
  const [devCost, setDevCost] = useState<MonthValue[]>([]);
  // その他経費
  const [otherCost, setOtherCost] = useState<MonthValue[]>([]);

  // 生産工場労務費単価
  const [productionFactoryLaborUnitPrice, setProductionFactoryLaborUnitPrice] = useState<number | null>(null);
  // 生産工場経費単価
  const [productionFactoryExpenseUnitPrice, setProductionFactoryExpenseUnitPrice] = useState<number | null>(null);
  // 年月毎費用データ
  const [monthlyExpenseData, setMonthlyExpenseData] = useState<MonthlyExpenseData[]>([]);
  // 年度毎費用の通貨ID（後方互換性のため残す）
  const [yearlyExpenseCurrencyId, setYearlyExpenseCurrencyId] = useState<string>("");
  // 各項目の通貨ID
  const [outsourcingProcessingCostCurrencyId, setOutsourcingProcessingCostCurrencyId] = useState<string>("");
  const [factoryBuildingDepreciationCurrencyId, setFactoryBuildingDepreciationCurrencyId] = useState<string>("");
  const [otherEquipmentCostCurrencyId, setOtherEquipmentCostCurrencyId] = useState<string>("");
  const [pastEquipmentCostCurrencyId, setPastEquipmentCostCurrencyId] = useState<string>("");
  const [domesticLaborCostCurrencyId, setDomesticLaborCostCurrencyId] = useState<string>("");
  const [domesticSgaExpensesCurrencyId, setDomesticSgaExpensesCurrencyId] = useState<string>("");
  const [productionSgaExpensesCurrencyId, setProductionSgaExpensesCurrencyId] = useState<string>("");
  const [domesticFactoryExpensesCurrencyId, setDomesticFactoryExpensesCurrencyId] = useState<string>("");
  const [salesCompanySgaCurrencyId, setSalesCompanySgaCurrencyId] = useState<string>("");
  const [devCostCurrencyId, setDevCostCurrencyId] = useState<string>("");
  const [royaltyCurrencyId, setRoyaltyCurrencyId] = useState<string>("");
  const [otherCostCurrencyId, setOtherCostCurrencyId] = useState<string>("");
  const [productionLaborCostCurrencyId, setProductionLaborCostCurrencyId] = useState<string>("");
  const [overseasFactoryExpensesCurrencyId, setOverseasFactoryExpensesCurrencyId] = useState<string>("");
  const [domesticInvestmentDepreciationCurrencyId, setDomesticInvestmentDepreciationCurrencyId] = useState<string>("");
  const [domesticFactoryBuildingDepreciationCurrencyId, setDomesticFactoryBuildingDepreciationCurrencyId] = useState<string>("");
  const [domesticOtherDepreciationCurrencyId, setDomesticOtherDepreciationCurrencyId] = useState<string>("");

  // どの部署の確認かを表す
  const [confirmSource, setConfirmSource] = useState<string>("");
  // 確認担当者
  const [confirmor, setConfirmor] = useState<Confirmor | null>(null);
  // 確認ボタン横幅
  const [buttonWidths, setButtonWidths] = useState<Record<string, number>>({});
  // 確認ボタン下担当者名
  const [names, setNames] = useState<Record<string, ConfirmorInfo[]>>({
    companyDivision: [],
    companyHeadDivision: [],
  });

  /** バリデーションエラー情報 */
  const [errors, setErrors] = useState<Errors>(clearErrors());

  /** 権限用 画面パーミッション */
  const [pagePermissions, setPagePermissions] = useState<PagePermissions>();

  /** Google Spread関連 */
  const [isGoogleSpreadLoading, setIsGoogleSpreadLoading] = useState<boolean>(false);
  const [isSyncDataLoading, setIsSyncDataLoading] = useState<boolean>(false);
  const [inputMode, setInputMode] = useState<"manual" | "google">("manual");
  const [spreadSheetWindow, setSpreadSheetWindow] = useState<Window | null>(null);

  /** 自動算出関連 */
  const [isAutoCalculating, setIsAutoCalculating] = useState<boolean>(false);

  // -----------------------------
  // 権限情報
  // -----------------------------
  const { userPermissions, reviewDate, currencyData } = useContext(ProjectContext) as ProjectContextType;

  // -----------------------------
  // 一時保存
  // -----------------------------
  // 一時保存対象（26項目）:
  //   費用データ: monthlyExpenseData / otherEquipmentCost / pastEquipmentCost / devCost / otherCost
  //   単価: productionFactoryLaborUnitPrice / productionFactoryExpenseUnitPrice / baseLaborCostIncreaseRate
  //   通貨ID（17項目）: yearlyExpenseCurrencyId / outsourcingProcessingCostCurrencyId /
  //     factoryBuildingDepreciationCurrencyId / otherEquipmentCostCurrencyId / pastEquipmentCostCurrencyId /
  //     domesticLaborCostCurrencyId / domesticSgaExpensesCurrencyId / productionSgaExpensesCurrencyId /
  //     domesticFactoryExpensesCurrencyId / salesCompanySgaCurrencyId / devCostCurrencyId /
  //     royaltyCurrencyId / otherCostCurrencyId / productionLaborCostCurrencyId /
  //     overseasFactoryExpensesCurrencyId / domesticInvestmentDepreciationCurrencyId /
  //     domesticFactoryBuildingDepreciationCurrencyId / domesticOtherDepreciationCurrencyId
  useImperativeHandle(ref, () => ({
    getSnapshot: () => {
      const data: Record<string, unknown> = {};
      if (monthlyExpenseData.length > 0) data.monthlyExpenseData = monthlyExpenseData;
      if (otherEquipmentCost.length > 0) data.otherEquipmentCost = otherEquipmentCost;
      if (pastEquipmentCost.length > 0) data.pastEquipmentCost = pastEquipmentCost;
      if (devCost.length > 0) data.devCost = devCost;
      if (otherCost.length > 0) data.otherCost = otherCost;
      if (productionFactoryLaborUnitPrice !== null) data.productionFactoryLaborUnitPrice = productionFactoryLaborUnitPrice;
      if (productionFactoryExpenseUnitPrice !== null) data.productionFactoryExpenseUnitPrice = productionFactoryExpenseUnitPrice;
      if (projectData?.baseLaborCostIncreaseRate !== null && projectData?.baseLaborCostIncreaseRate !== undefined) data.baseLaborCostIncreaseRate = projectData.baseLaborCostIncreaseRate;
      if (yearlyExpenseCurrencyId) data.yearlyExpenseCurrencyId = yearlyExpenseCurrencyId;
      if (outsourcingProcessingCostCurrencyId) data.outsourcingProcessingCostCurrencyId = outsourcingProcessingCostCurrencyId;
      if (factoryBuildingDepreciationCurrencyId) data.factoryBuildingDepreciationCurrencyId = factoryBuildingDepreciationCurrencyId;
      if (otherEquipmentCostCurrencyId) data.otherEquipmentCostCurrencyId = otherEquipmentCostCurrencyId;
      if (pastEquipmentCostCurrencyId) data.pastEquipmentCostCurrencyId = pastEquipmentCostCurrencyId;
      if (domesticLaborCostCurrencyId) data.domesticLaborCostCurrencyId = domesticLaborCostCurrencyId;
      if (domesticSgaExpensesCurrencyId) data.domesticSgaExpensesCurrencyId = domesticSgaExpensesCurrencyId;
      if (productionSgaExpensesCurrencyId) data.productionSgaExpensesCurrencyId = productionSgaExpensesCurrencyId;
      if (domesticFactoryExpensesCurrencyId) data.domesticFactoryExpensesCurrencyId = domesticFactoryExpensesCurrencyId;
      if (salesCompanySgaCurrencyId) data.salesCompanySgaCurrencyId = salesCompanySgaCurrencyId;
      if (devCostCurrencyId) data.devCostCurrencyId = devCostCurrencyId;
      if (royaltyCurrencyId) data.royaltyCurrencyId = royaltyCurrencyId;
      if (otherCostCurrencyId) data.otherCostCurrencyId = otherCostCurrencyId;
      if (productionLaborCostCurrencyId) data.productionLaborCostCurrencyId = productionLaborCostCurrencyId;
      if (overseasFactoryExpensesCurrencyId) data.overseasFactoryExpensesCurrencyId = overseasFactoryExpensesCurrencyId;
      if (domesticInvestmentDepreciationCurrencyId) data.domesticInvestmentDepreciationCurrencyId = domesticInvestmentDepreciationCurrencyId;
      if (domesticFactoryBuildingDepreciationCurrencyId) data.domesticFactoryBuildingDepreciationCurrencyId = domesticFactoryBuildingDepreciationCurrencyId;
      if (domesticOtherDepreciationCurrencyId) data.domesticOtherDepreciationCurrencyId = domesticOtherDepreciationCurrencyId;
      return data;
    },
    applySnapshot: (data: Record<string, unknown>) => {
      const filterMonthValues = (snap: { year: number; month: number; value: number }[]) =>
        fiscalMonthsLabelless.map(({ year, month }) => {
          const found = snap.find(s => s.year === year && s.month === month);
          return { year, month, value: found?.value ?? 0 };
        });

      if (data.monthlyExpenseData !== undefined) {
        const snap = data.monthlyExpenseData as typeof monthlyExpenseData;
        setMonthlyExpenseData(fiscalMonthsLabelless.map(({ year, month }) => {
          const found = snap.find(s => s.year === year && s.month === month);
          return {
            year, month,
            outsourcingProcessingCost: found?.outsourcingProcessingCost ?? 0,
            productionLaborCost: found?.productionLaborCost ?? 0,
            domesticLaborCost: found?.domesticLaborCost ?? 0,
            overseasFactoryExpenses: found?.overseasFactoryExpenses ?? 0,
            factoryBuildingDepreciation: found?.factoryBuildingDepreciation ?? 0,
            pastEquipmentCost: found?.pastEquipmentCost ?? 0,
            otherEquipmentCost: found?.otherEquipmentCost ?? 0,
            domesticFactoryExpenses: found?.domesticFactoryExpenses ?? 0,
            domesticInvestmentDepreciation: found?.domesticInvestmentDepreciation ?? 0,
            domesticFactoryBuildingDepreciation: found?.domesticFactoryBuildingDepreciation ?? 0,
            domesticOtherDepreciation: found?.domesticOtherDepreciation ?? 0,
            productionSgaExpenses: found?.productionSgaExpenses ?? 0,
            domesticSgaExpenses: found?.domesticSgaExpenses ?? 0,
            salesCompanySga: found?.salesCompanySga ?? 0,
            devCost: found?.devCost ?? 0,
            royalty: found?.royalty ?? 0,
            otherCost: found?.otherCost ?? 0,
          };
        }));
      }
      if (data.otherEquipmentCost !== undefined) setOtherEquipmentCost(filterMonthValues(data.otherEquipmentCost as { year: number; month: number; value: number }[]));
      if (data.pastEquipmentCost !== undefined) setPastEquipmentCost(filterMonthValues(data.pastEquipmentCost as { year: number; month: number; value: number }[]));
      if (data.devCost !== undefined) setDevCost(filterMonthValues(data.devCost as { year: number; month: number; value: number }[]));
      if (data.otherCost !== undefined) setOtherCost(filterMonthValues(data.otherCost as { year: number; month: number; value: number }[]));
      if (data.productionFactoryLaborUnitPrice !== undefined) setProductionFactoryLaborUnitPrice(data.productionFactoryLaborUnitPrice as number);
      if (data.productionFactoryExpenseUnitPrice !== undefined) setProductionFactoryExpenseUnitPrice(data.productionFactoryExpenseUnitPrice as number);
      if (data.baseLaborCostIncreaseRate !== undefined) setProjectData(prev => prev ? { ...prev, baseLaborCostIncreaseRate: data.baseLaborCostIncreaseRate as number } : prev);
      if (data.yearlyExpenseCurrencyId !== undefined) setYearlyExpenseCurrencyId(data.yearlyExpenseCurrencyId as string);
      if (data.outsourcingProcessingCostCurrencyId !== undefined) setOutsourcingProcessingCostCurrencyId(data.outsourcingProcessingCostCurrencyId as string);
      if (data.factoryBuildingDepreciationCurrencyId !== undefined) setFactoryBuildingDepreciationCurrencyId(data.factoryBuildingDepreciationCurrencyId as string);
      if (data.otherEquipmentCostCurrencyId !== undefined) setOtherEquipmentCostCurrencyId(data.otherEquipmentCostCurrencyId as string);
      if (data.pastEquipmentCostCurrencyId !== undefined) setPastEquipmentCostCurrencyId(data.pastEquipmentCostCurrencyId as string);
      if (data.domesticLaborCostCurrencyId !== undefined) setDomesticLaborCostCurrencyId(data.domesticLaborCostCurrencyId as string);
      if (data.domesticSgaExpensesCurrencyId !== undefined) setDomesticSgaExpensesCurrencyId(data.domesticSgaExpensesCurrencyId as string);
      if (data.productionSgaExpensesCurrencyId !== undefined) setProductionSgaExpensesCurrencyId(data.productionSgaExpensesCurrencyId as string);
      if (data.domesticFactoryExpensesCurrencyId !== undefined) setDomesticFactoryExpensesCurrencyId(data.domesticFactoryExpensesCurrencyId as string);
      if (data.salesCompanySgaCurrencyId !== undefined) setSalesCompanySgaCurrencyId(data.salesCompanySgaCurrencyId as string);
      if (data.devCostCurrencyId !== undefined) setDevCostCurrencyId(data.devCostCurrencyId as string);
      if (data.royaltyCurrencyId !== undefined) setRoyaltyCurrencyId(data.royaltyCurrencyId as string);
      if (data.otherCostCurrencyId !== undefined) setOtherCostCurrencyId(data.otherCostCurrencyId as string);
      if (data.productionLaborCostCurrencyId !== undefined) setProductionLaborCostCurrencyId(data.productionLaborCostCurrencyId as string);
      if (data.overseasFactoryExpensesCurrencyId !== undefined) setOverseasFactoryExpensesCurrencyId(data.overseasFactoryExpensesCurrencyId as string);
      if (data.domesticInvestmentDepreciationCurrencyId !== undefined) setDomesticInvestmentDepreciationCurrencyId(data.domesticInvestmentDepreciationCurrencyId as string);
      if (data.domesticFactoryBuildingDepreciationCurrencyId !== undefined) setDomesticFactoryBuildingDepreciationCurrencyId(data.domesticFactoryBuildingDepreciationCurrencyId as string);
      if (data.domesticOtherDepreciationCurrencyId !== undefined) setDomesticOtherDepreciationCurrencyId(data.domesticOtherDepreciationCurrencyId as string);
    },
  }));

  // -----------------------------
  // useDisclosure
  // -----------------------------
  const {
    isOpen: isSpanConfirmOpen,
    onOpen: onSpanConfirmOpen,
    onClose: onSpanConfirmClose
  } = useDisclosure();

  /** 帳票ファイルアップロードモーダル制御用 */
  const { isOpen: isOpenReportUploadModal, onOpen: onOpenReportUploadModal, onClose: onCloseReportUploadModal } = useDisclosure();

  /** PLSファイルアップロードモーダル制御用 */
  const { isOpen: isOpenPLSUploadModal, onOpen: onOpenPLSUploadModal, onClose: onClosePLSUploadModal } = useDisclosure();

  // -----------------------------
  // useEffect
  // -----------------------------

  /**
   * 権限情報
   */
  useEffect(() => {
    const pagePermissions = getPermissions(userPermissions, WorkflowId.ProfitAndLoss, projectData?.purpose, employeeId, divisionId, projectData?.registrantDivisionId);
    setPagePermissions(pagePermissions);
  }, [userPermissions, employeeId, divisionId, projectData]);

  /** 画面初期表示時のデータ取得 */
  useEffect(() => {
    console.log("ProfitAndLoss start");
    getDispData();
  }, []);

  /**
   * 画面遷移時にGoogleSpreadタブを閉じる
   */
  useEffect(() => {
    return () => {
      // コンポーネントのアンマウント時にGoogleSpreadタブを閉じる
      if (spreadSheetWindow && !spreadSheetWindow.closed) {
        spreadSheetWindow.close();
      }
    };
  }, [spreadSheetWindow]);

  /**
   * エラー情報初期化
   * @returns エラー情報初期状態
   */
  function clearErrors() {
    return {
      /** 労務費のベースアップ率(%) */
      baseLaborCostIncreaseRate: { isError: false, errorMessage: "", },
      /** 生産工場労務費単価 */
      productionFactoryLaborUnitPrice: { isError: false, errorMessage: "", },
      /** 生産工場経費単価 */
      productionFactoryExpenseUnitPrice: { isError: false, errorMessage: "", },
      /** 費用項目の通貨 */
      expenseCurrency: { isError: false, errorMessage: "", },
      /** 自動算出エラー */
      autoCalculation: { isError: false, errorMessage: "", },
    };
  }

  /** 画面初期表示時 */
  const getDispData = async () => {
    try {
      if (projectId && projectId !== "") {
        const projectResult = await getProjectData();
      } else {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ProfitAndLoss.message.registerFromBasicInfo'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('getDispData:', error);
      toast({
        title: TITLE,
        description: t('common.api.commonError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  /** 通貨情報設定(ローカル通貨) */
  useEffect(() => {
    // 通貨設定（会社/工場の通貨を優先、なければ設置工場）
    if (localCurrencyId && currencyData.length > 0) {
      const selected = currencyData.find(currency => currency.currencyId === localCurrencyId);
      if (selected && selected.currencyId && selected.currencyName) {
        setSelectedCurrency({
          id: selected.currencyId,
          name: selected.currencyName,
          exchangeRate: selected.exchangeRate ?? null,
        });
        // 費用項目の通貨IDが未設定の場合はローカル通貨をデフォルトに設定
        if (!outsourcingProcessingCostCurrencyId) {
          setOutsourcingProcessingCostCurrencyId(selected.currencyId);
        }
        if (!productionLaborCostCurrencyId) {
          setProductionLaborCostCurrencyId(selected.currencyId);
        }
        if (!domesticLaborCostCurrencyId) {
          setDomesticLaborCostCurrencyId(selected.currencyId);
        }
        if (!overseasFactoryExpensesCurrencyId) {
          setOverseasFactoryExpensesCurrencyId(selected.currencyId);
        }
        if (!factoryBuildingDepreciationCurrencyId) {
          setFactoryBuildingDepreciationCurrencyId(selected.currencyId);
        }
        if (!otherEquipmentCostCurrencyId) {
          setOtherEquipmentCostCurrencyId(selected.currencyId);
        }
        if (!pastEquipmentCostCurrencyId) {
          setPastEquipmentCostCurrencyId(selected.currencyId);
        }
        if (!domesticFactoryExpensesCurrencyId) {
          setDomesticFactoryExpensesCurrencyId(selected.currencyId);
        }
        if (!domesticInvestmentDepreciationCurrencyId) {
          setDomesticInvestmentDepreciationCurrencyId(selected.currencyId);
        }
        if (!domesticFactoryBuildingDepreciationCurrencyId) {
          setDomesticFactoryBuildingDepreciationCurrencyId(selected.currencyId);
        }
        if (!domesticOtherDepreciationCurrencyId) {
          setDomesticOtherDepreciationCurrencyId(selected.currencyId);
        }
        if (!productionSgaExpensesCurrencyId) {
          setProductionSgaExpensesCurrencyId(selected.currencyId);
        }
        if (!domesticSgaExpensesCurrencyId) {
          setDomesticSgaExpensesCurrencyId(selected.currencyId);
        }
        if (!salesCompanySgaCurrencyId) {
          setSalesCompanySgaCurrencyId(selected.currencyId);
        }
        if (!devCostCurrencyId) {
          setDevCostCurrencyId(selected.currencyId);
        }
        if (!royaltyCurrencyId) {
          setRoyaltyCurrencyId(selected.currencyId);
        }
        if (!otherCostCurrencyId) {
          setOtherCostCurrencyId(selected.currencyId);
        }
      }
    }
  }, [localCurrencyId, currencyData]);

  /** 画面初期表示時：過去PJ設備費が未登録なら自動算出 */
  useEffect(() => {
    if (
      projectData?.pastProjectId &&
      selectedCurrency.name &&
      currencyData.length > 0 &&
      fiscalMonthsLabelless.length > 0 &&
      pastEquipmentCost.every(c => c.value === 0 || c.value === null || c.value === undefined)
    ) {
      calcPastEquipmentDepreciation().catch((err) => {
        console.error("初期表示時の過去PJ設備減価償却費算出エラー:", err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectData?.pastProjectId, selectedCurrency.name, currencyData, fiscalMonthsLabelless]);

  useEffect(() => {
    const widths: Record<string, number> = Object.keys(refs).reduce((acc, key) => {
      acc[key] = refs[key]?.current?.offsetWidth || 0;
      return acc;
    }, {} as Record<string, number>);
    setButtonWidths(widths);
  }, []);

  useEffect(() => {
    const fetchConfirmor = async () => {
      try {
        // 確認担当者JSONを取得
        const confirmorData = await getConfirmor(
          WebAPI,
          projectId,
          reviewDate,
        );
        setConfirmor(confirmorData);
      } catch (error) {
        // TODO 処理失敗時は暫定でスルーする
      }
    };
    if (projectId) {
      fetchConfirmor();
    }
  }, [projectId, reviewDate]);

  useEffect(() => {
    if (confirmor?.profitAndLoss) {
      const updatedNames = Object.keys(names).reduce((acc, division) => {
        const divisionData = confirmor.profitAndLoss?.[division as keyof Confirmor['profitAndLoss']];
        if (Array.isArray(divisionData)) {
          acc[division] = divisionData;
        } else {
          acc[division] = [];
        }
        return acc;
      }, {} as Record<string, ConfirmorInfo[]>);
      setNames(updatedNames);
    }
  }, [confirmor]);

  useEffect(() => {
    if (!currencyData) { return; }
    if (!uploadedPLFileContents || uploadedPLFileContents.length === 0) { return; }
    if (!selectedCurrency.name) { return; }
    // 複数ファイルをパースしてそれぞれの結果を保持
    fileParserMultiple(uploadedPLFileContents);
  }, [uploadedPLFileContents, currencyData, selectedCurrency]);

  /**
   * 初期表示
   */
  useEffect(() => {
    /**
     * GAPIロードとサインインを行う。
     */
    const initGapi = async () => {
      try {
        // Google API 初期化
        if (!await googleAPI.initGapi()) {
          throw new Error("initGapi error");
        }

        // Googleアカウント認証
        if (! await googleAPI.signIn()) {
          throw new Error("signIn error");
        }
      } catch (error) {
        console.error("Google API 初期化エラー:", error);
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ProfitAndLoss.message.googleApiInitFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    if (projectId) {
      // GAPIロードとサインイン
      initGapi();
    }
  }, [toast]);

  // -----------------------------
  // API
  // -----------------------------
  /** プロジェクト情報取得API */
  const getProjectData = async () => {
    try {
      console.log(`getProjectData():start`);
      const payload = {
        query: `
          query getProjectData($projectId: String, $useRDSData: Boolean, $reviewDate: String) {
            getProjectById(projectId: $projectId, useRDSData: $useRDSData, reviewDate: $reviewDate) {
              projectId
              revisedOpportunityNameByRevision
              divisionId
              baseLaborCostIncreaseRate

              otherEquipmentCost
              pastEquipmentCost
              pastEquipmentCostCurrencyId
              devCost
              otherCost

              productionFactoryLaborUnitPrice
              productionFactoryExpenseUnitPrice
              monthlyExpenseData
              yearlyExpenseCurrencyId
              outsourcingProcessingCostCurrencyId
              factoryBuildingDepreciationCurrencyId
              otherEquipmentCostCurrencyId
              domesticLaborCostCurrencyId
              domesticSgaExpensesCurrencyId
              productionSgaExpensesCurrencyId
              domesticFactoryExpensesCurrencyId
              salesCompanySgaCurrencyId
              devCostCurrencyId
              royaltyCurrencyId
              otherCostCurrencyId
              productionLaborCostCurrencyId
              overseasFactoryExpensesCurrencyId
              domesticInvestmentDepreciationCurrencyId
              domesticFactoryBuildingDepreciationCurrencyId
              domesticOtherDepreciationCurrencyId

              moldSalesByProject
              developmentSalesByProject
              equipmentSalesByProject

              projectStartDate
              projectEndDate
              purpose
              applicationFormType
              pastProjectId
              pastReviewDate
              equipmentUsageType
              createDate
              updateDate
              companyPlantSnapshot
              # RDS Data
              opportunityName
              registrantDivisionId
              updateDateRDS
            }
            getPartNo(projectId: $projectId, reviewDate: $reviewDate) {
              partNo
              displayPartNo
              quantityAndPrice
            }
          }`,
        variables: {
          projectId: projectId,
          useRDSData: true,
          ...(reviewDate && { reviewDate })
        }
      };
      // プロジェクト情報取得（dynamodb、RDS）
      const response = await WebAPI.request(payload);

      let partNoInfo: PartNo[] = [];
      if (response?.data?.getPartNo) {
        partNoInfo = response?.data?.getPartNo;
      }

      // データ取得に成功した場合
      if (response && response.data && response.data.getProjectById) {

        if (!response.data.getProjectById.projectId) {
          // データ取得失敗の場合
          toast({
            title: TITLE,
            description: t('features.InvestmentScreening.ProfitAndLoss.message.projectFetchFailed'),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });

          console.error(`Failed to call API getProjectById. errors = ${JSON.stringify(response?.errors)}`);
          return;
        }

        const projectInfo = response.data.getProjectById;
        setProjectData(projectInfo);

        if (partNoInfo && partNoInfo.length > 0) {
          // 総会計年度数の取得（PartNo, PJ別金型費回収, PJ別開発費回収, PJ別設備費回収を参照）
          const totalFiscalYearsByProject = getTotalFiscalYearsByProject(partNoInfo, projectInfo.moldSalesByProject, projectInfo.developmentSalesByProject, projectInfo.equipmentSalesByProject);


          // 年月文字列配列の取得
          const fiscalMonthsStringAll = getFiscalMonthsString((projectInfo.projectStartDate || dayjs().format("YYYY-MM-DD")), totalFiscalYearsByProject);
          // 年月情報配列の取得
          const fiscalMonthsLabellessAll = getFiscalMonthsString((projectInfo.projectStartDate || dayjs().format("YYYY-MM-DD")), totalFiscalYearsByProject, false);

          // EOL予定日の属する年度の3月末までをフィルタリング（年度単位で制御）
          const filterByEOL = <T,>(items: T[], labellessItems: { year: number; month: number }[]): T[] => {
            if (!projectInfo.projectEndDate) return items;
            const eolYear = parseInt(projectInfo.projectEndDate.split("-")[0], 10);
            const eolMonth = parseInt(projectInfo.projectEndDate.split("-")[1], 10);

            // EOL予定日の属する年度の年度末（3月）を計算
            // 4月〜12月 → 翌年3月が年度末
            // 1月〜3月 → 同年3月が年度末
            const fiscalYearEndYear = eolMonth >= 4 ? eolYear + 1 : eolYear;
            const fiscalYearEndYearMonth = fiscalYearEndYear * 100 + 3; // 3月

            return items.filter((_, idx) => {
              const item = labellessItems[idx] as { year: number; month: number };
              const currentYearMonth = item.year * 100 + item.month;
              return currentYearMonth <= fiscalYearEndYearMonth;
            });
          };

          const fiscalMonthsString = filterByEOL(fiscalMonthsStringAll as string[], fiscalMonthsLabellessAll as { year: number; month: number }[]);
          setFiscalMonthsString(fiscalMonthsString);
          const fiscalMonthsLabelless = filterByEOL(fiscalMonthsLabellessAll as { year: number; month: number }[], fiscalMonthsLabellessAll as { year: number; month: number }[]);
          setFiscalMonthsLabelless(fiscalMonthsLabelless);

          // デフォルト値/既存データ設定処理
          const getCostData = (jsonData: string) => {
            let tempCostData: MonthValue[] = [];

            if (jsonData) {
              // 既存データがあれば、パース
              tempCostData = JSON.parse(jsonData);
            }

            const costData = fiscalMonthsLabelless.map((item) => {
              const { year, month } = item as { year: number; month: number; };

              const target = tempCostData.find(
                (m) => m.year === year && m.month === month
              );

              // EOL予定日を超えているか判定
              const isAfterEolDate = (): boolean => {
                if (!projectInfo.projectEndDate) return false;
                const eolYear = parseInt(projectInfo.projectEndDate.split("-")[0], 10);
                const eolMonth = parseInt(projectInfo.projectEndDate.split("-")[1], 10);
                const currentYearMonth = year * 100 + month;
                const eolYearMonth = eolYear * 100 + eolMonth;
                return currentYearMonth > eolYearMonth;
              };

              const afterEol = isAfterEolDate();

              return {
                year,
                month,
                value: afterEol ? 0 : (target?.value ?? 0),
              };
            });

            return costData;
          }

          // その他の設備費
          const otherEquipmentCost = getCostData(projectInfo.otherEquipmentCost);
          setOtherEquipmentCost(otherEquipmentCost);

          // 過去PJの投資対象設備費
          const pastEquipmentCostData = getCostData(projectInfo.pastEquipmentCost);
          setPastEquipmentCost(pastEquipmentCostData);

          // 開発費
          const devCost = getCostData(projectInfo.devCost);
          setDevCost(devCost);

          // その他経費
          const otherCost = getCostData(projectInfo.otherCost);
          setOtherCost(otherCost);

          // 年月毎費用データ
          const getMonthlyExpenseData = (jsonData: string): MonthlyExpenseData[] => {
            let tempData: MonthlyExpenseData[] = [];
            if (jsonData || null) {
              tempData = JSON.parse(jsonData);
            }

            return fiscalMonthsLabelless.map((item) => {
              const { year, month } = item as { year: number; month: number; };
              const target = tempData.find(
                (m) => m.year === year && m.month === month
              );

              // EOL予定日を超えているか判定
              const isAfterEolDate = (): boolean => {
                if (!projectInfo.projectEndDate) return false;
                const eolYear = parseInt(projectInfo.projectEndDate.split("-")[0], 10);
                const eolMonth = parseInt(projectInfo.projectEndDate.split("-")[1], 10);
                const currentYearMonth = year * 100 + month;
                const eolYearMonth = eolYear * 100 + eolMonth;
                return currentYearMonth > eolYearMonth;
              };

              const afterEol = isAfterEolDate();

              return {
                year,
                month,
                // 画面表示順
                outsourcingProcessingCost: afterEol ? 0 : (target?.outsourcingProcessingCost ?? 0),
                productionLaborCost: afterEol ? 0 : (target?.productionLaborCost ?? 0),
                domesticLaborCost: afterEol ? 0 : (target?.domesticLaborCost ?? 0),
                overseasFactoryExpenses: afterEol ? 0 : (target?.overseasFactoryExpenses ?? 0),
                factoryBuildingDepreciation: afterEol ? 0 : (target?.factoryBuildingDepreciation ?? 0),
                pastEquipmentCost: afterEol ? 0 : (target?.pastEquipmentCost ?? 0),
                otherEquipmentCost: afterEol ? 0 : (target?.otherEquipmentCost ?? 0),
                domesticFactoryExpenses: afterEol ? 0 : (target?.domesticFactoryExpenses ?? 0),
                domesticInvestmentDepreciation: afterEol ? 0 : (target?.domesticInvestmentDepreciation ?? 0),
                domesticFactoryBuildingDepreciation: afterEol ? 0 : (target?.domesticFactoryBuildingDepreciation ?? 0),
                domesticOtherDepreciation: afterEol ? 0 : (target?.domesticOtherDepreciation ?? 0),
                productionSgaExpenses: afterEol ? 0 : (target?.productionSgaExpenses ?? 0),
                domesticSgaExpenses: afterEol ? 0 : (target?.domesticSgaExpenses ?? 0),
                salesCompanySga: afterEol ? 0 : (target?.salesCompanySga ?? 0),
                devCost: afterEol ? 0 : (target?.devCost ?? 0),
                royalty: afterEol ? 0 : (target?.royalty ?? 0),
                otherCost: afterEol ? 0 : (target?.otherCost ?? 0),
              };
            });
          };

          const monthlyExpenseDataParsed = getMonthlyExpenseData(projectInfo.monthlyExpenseData);
          setMonthlyExpenseData(monthlyExpenseDataParsed);
        }

        // 生産工場労務費単価、生産工場経費単価
        setProductionFactoryLaborUnitPrice(projectInfo.productionFactoryLaborUnitPrice ?? null);
        setProductionFactoryExpenseUnitPrice(projectInfo.productionFactoryExpenseUnitPrice ?? null);
        // 年度毎費用の通貨ID
        setYearlyExpenseCurrencyId(projectInfo.yearlyExpenseCurrencyId ?? "");
        // 各項目の通貨ID
        setOutsourcingProcessingCostCurrencyId(projectInfo.outsourcingProcessingCostCurrencyId ?? "");
        setFactoryBuildingDepreciationCurrencyId(projectInfo.factoryBuildingDepreciationCurrencyId ?? "");
        setOtherEquipmentCostCurrencyId(projectInfo.otherEquipmentCostCurrencyId ?? "");
        setPastEquipmentCostCurrencyId(projectInfo.pastEquipmentCostCurrencyId ?? "");
        setDomesticLaborCostCurrencyId(projectInfo.domesticLaborCostCurrencyId ?? "");
        setDomesticSgaExpensesCurrencyId(projectInfo.domesticSgaExpensesCurrencyId ?? "");
        setProductionSgaExpensesCurrencyId(projectInfo.productionSgaExpensesCurrencyId ?? "");
        setDomesticFactoryExpensesCurrencyId(projectInfo.domesticFactoryExpensesCurrencyId ?? "");
        setSalesCompanySgaCurrencyId(projectInfo.salesCompanySgaCurrencyId ?? "");
        setDevCostCurrencyId(projectInfo.devCostCurrencyId ?? "");
        setRoyaltyCurrencyId(projectInfo.royaltyCurrencyId ?? "");
        setOtherCostCurrencyId(projectInfo.otherCostCurrencyId ?? "");
        setProductionLaborCostCurrencyId(projectInfo.productionLaborCostCurrencyId ?? "");
        setOverseasFactoryExpensesCurrencyId(projectInfo.overseasFactoryExpensesCurrencyId ?? "");
        // 新レイアウト用の通貨ID（国内工場経費 > 減価償却費）
        setDomesticInvestmentDepreciationCurrencyId(projectInfo.domesticInvestmentDepreciationCurrencyId ?? "");
        setDomesticFactoryBuildingDepreciationCurrencyId(projectInfo.domesticFactoryBuildingDepreciationCurrencyId ?? "");
        setDomesticOtherDepreciationCurrencyId(projectInfo.domesticOtherDepreciationCurrencyId ?? "");

        // P&Lファイルデータ取得
        await fetchPLFileData();

      } else {
        // データ取得失敗の場合
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ProfitAndLoss.message.projectFetchFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error in getProjectData:", error);
      return false;
    }
  };


  /** P&Lファイルデータ取得API（複数ファイル対応）
   *  振り返りモード時は reviewDate を渡して申請時分＋振り返り追加分の累積を取得する
   */
  const fetchPLFileData = async () => {
    try {
      const result = await WebAPI.request({
        query: `
          query getFileInfoFromS3($folderName: String, $withUrl: Boolean, $reviewDate: String) {
            getFileInfoFromS3(folderName: $folderName, withUrl: $withUrl, reviewDate: $reviewDate) {
              folderName
              files {
                key
                fileName
                lastModified
                size
                url
                scanStatus
              }
            }
          }
        `,
        variables: {
          folderName: `${projectId}/ProfitAndLoss`,
          withUrl: false,
          ...(reviewDate && { reviewDate }),
        },
      });

      const folders = result?.data?.getFileInfoFromS3 || [];

      if (folders.length === 0) {
        // ファイルがない場合は初期化
        setUploadedPLFileContents([]);
        setProfitAndLossFileName(null);
        setFiscalDataList([]);
        setDateRangeLabelList([]);
        return;
      }

      const files = folders[0].files.map((f: any) => ({
        key: f.key,
        fileName: f.fileName,
        lastModified: f.lastModified,
        size: f.size,
        file: undefined,
        scanStatus: f.scanStatus ?? undefined,
      }));

      // P&Lファイル（複数対応）
      if (files && files.length > 0) {
        const base64Array = await fetchAllBase64FromS3(files);

        // ファイル名とコンテンツをセットで保持
        // マルウェア検知ファイルはBase64が取得できないため、取得できたファイルのみを対象とする
        const fileContents = files
          .map((f: UploadedFile, index: number) => ({
            fileName: f.fileName,
            content: base64Array[index],
          }))
          .filter((c: { fileName: string; content: string | null }): c is { fileName: string; content: string } => !!c.content);
        setUploadedPLFileContents(fileContents);

        const blockedFiles = files.filter((f: UploadedFile) => isScanBlocked(f.scanStatus));
        if (blockedFiles.length > 0) {
          toast({
            title: TITLE,
            description: t('features.InvestmentScreening.ProfitAndLoss.message.plFileScanBlocked', {
              fileNames: blockedFiles.map((f: UploadedFile) => f.fileName).join(', '),
            }),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }

        // 選択P&Lファイル名（最初のファイル名を表示用に設定）
        setProfitAndLossFileName(files.length === 1 ? files[0].fileName : t('features.InvestmentScreening.ProfitAndLoss.label.fileCount', { count: files.length }));
      }
    } catch (e) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ProfitAndLoss.message.plFileFetchFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error("Error P&L get file:", e);
    }
  };

  // 費用データを保存
  const saveProfitAndLoss = async (e: React.MouseEvent<HTMLButtonElement>) => {

    // フォーカスを外す
    e.currentTarget.blur();

    try {
      // monthlyExpenseDataに別管理の項目の値をマージ
      const monthlyExpenseDataWithExtras = monthlyExpenseData.map((data, idx) => ({
        ...data,
        pastEquipmentCost: pastEquipmentCost[idx]?.value ?? 0,
        otherEquipmentCost: otherEquipmentCost[idx]?.value ?? 0,
        devCost: devCost[idx]?.value ?? 0,
        otherCost: otherCost[idx]?.value ?? 0,
      }));

      const input = {
        projectId: projectId,

        otherEquipmentCost: JSON.stringify(otherEquipmentCost),
        pastEquipmentCost: JSON.stringify(pastEquipmentCost),
        devCost: JSON.stringify(devCost),
        otherCost: JSON.stringify(otherCost),

        productionFactoryLaborUnitPrice: productionFactoryLaborUnitPrice,
        productionFactoryExpenseUnitPrice: productionFactoryExpenseUnitPrice,
        monthlyExpenseData: JSON.stringify(monthlyExpenseDataWithExtras),
        yearlyExpenseCurrencyId: yearlyExpenseCurrencyId,
        outsourcingProcessingCostCurrencyId: outsourcingProcessingCostCurrencyId,
        factoryBuildingDepreciationCurrencyId: factoryBuildingDepreciationCurrencyId,
        otherEquipmentCostCurrencyId: otherEquipmentCostCurrencyId,
        pastEquipmentCostCurrencyId: pastEquipmentCostCurrencyId,
        domesticLaborCostCurrencyId: domesticLaborCostCurrencyId,
        domesticSgaExpensesCurrencyId: domesticSgaExpensesCurrencyId,
        productionSgaExpensesCurrencyId: productionSgaExpensesCurrencyId,
        domesticFactoryExpensesCurrencyId: domesticFactoryExpensesCurrencyId,
        salesCompanySgaCurrencyId: salesCompanySgaCurrencyId,
        devCostCurrencyId: devCostCurrencyId,
        royaltyCurrencyId: royaltyCurrencyId,
        otherCostCurrencyId: otherCostCurrencyId,
        productionLaborCostCurrencyId: productionLaborCostCurrencyId,
        overseasFactoryExpensesCurrencyId: overseasFactoryExpensesCurrencyId,
        domesticInvestmentDepreciationCurrencyId: domesticInvestmentDepreciationCurrencyId,
        domesticFactoryBuildingDepreciationCurrencyId: domesticFactoryBuildingDepreciationCurrencyId,
        domesticOtherDepreciationCurrencyId: domesticOtherDepreciationCurrencyId,

        baseLaborCostIncreaseRate: projectData?.baseLaborCostIncreaseRate,
        updateDate: projectData?.updateDate,
        ...(reviewDate && { reviewDate }),
      };

      // DB更新
      const query = `
        mutation updateProfitAndLoss ($input: ProfitAndLossInput!) {
          updateProfitAndLoss(input: $input) {status message}
        }
      `;
      const result = await WebAPI.request({
        query: query,
        variables: { input }
      });

      if (result?.data?.updateProfitAndLoss.status === 'success') {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ProfitAndLoss.message.expenseSaveSuccess'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        console.log("updateProfitAndLoss successfully");

        // 再取得
        await getProjectData();
      } else if (result?.data?.updateProfitAndLoss.status === 'expired') {
        toast({
          title: TITLE,
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        console.warn(`Conflict error. updateProfitAndLoss.`);
      } else {
        throw new Error(result);
      }
    } catch (error) {
      toast({
        title: TITLE,
        description: t('common.api.commonError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error("Error updating P&L:", error);
    }

    try {
      // ワークフローテーブルを更新
      await updateConfirm(
        WebAPI,
        projectId,
        WorkflowId.ProfitAndLoss,
        employeeId,
        confirmSource,
        "true",
        reviewDate,
      );
      // 確認担当者JSONを取得
      const confirmor = await getConfirmor(
        WebAPI,
        projectId,
        reviewDate,
      );
      setConfirmor(confirmor);
    } catch (error) {
      // TODO 処理失敗時は暫定でスルーする
    }
  }

  /**
   * 指定ファイルのbase64を取得
   */
  const fetchAllBase64FromS3 = async (files: UploadedFile[] | undefined) => {

    if (!files) return [];

    // マルウェア検知ファイルはバックエンドがエラーを返すため、1件の失敗でP&Lファイル
    // 読み込み全体が失敗しないよう allSettled で個別に扱う（インデックス整合は維持する）
    const results = await Promise.allSettled(
      files.map(f =>
        WebAPI.request({
          query: `
          query getBase64FromS3($key: String!) {
            getBase64FromS3(key: $key)
          }
        `,
          variables: { key: f.key },
        }).then(res => res?.data?.getBase64FromS3)
      )
    );

    return results.map((result, index): string | null => {
      if (result.status === "fulfilled" && result.value) {
        return result.value;
      }
      // 取得できなかったファイルは読み込みを諦めて継続する
      console.warn(
        `Failed to fetch Base64, skipped: key=${files[index].key}, scanStatus=${files[index].scanStatus ?? "undefined"}`,
        result.status === "rejected" ? result.reason : undefined
      );
      return null;
    });
  };

  /** 年月毎費用データ変更ハンドラ */
  const handleMonthlyExpenseChange = (
    index: number,
    field: keyof MonthlyExpenseData,
    value: number
  ) => {
    setMonthlyExpenseData((prev) => {
      const newData = [...prev];
      if (newData[index]) {
        newData[index] = {
          ...newData[index],
          [field]: value,
        };
      }
      return newData;
    });
  };

  /**
   * 「画面で入力」ボタン押下
   */
  const handleManualClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();
    // スプレッドシートClose
    spreadSheetWindow?.close();
  };

  // 項目名ラベルを表示ロケールに応じて取得するヘルパー関数
  const rowLabel = (key: string): string => t(`features.InvestmentScreening.ProfitAndLoss.row.${key}`);

  /**
   * 「GoogleSpread」ボタン押下
   * スプレッドシートを作成し、費用入力テーブルのデータを書き込む
   */
  const handleGoogleSpreadClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();

    if (inputMode === "google") {
      return;
    }

    if (isGoogleSpreadLoading) {
      return;
    }

    try {
      setIsGoogleSpreadLoading(true);

      // ポップアップブロック回避：非同期処理より前にユーザー操作起因でウィンドウを確保
      const newWindow = window.open("", "_blank");
      setSpreadSheetWindow(newWindow);

      // Googleアカウント認証
      if (!await googleAPI.signIn()) {
        throw new Error("signIn error");
      }

      // スプレッドシートファイル名（プロジェクトIDを使用）
      const fileName = `ProfitAndLoss_${projectId}`;
      const folderPath = PROFIT_AND_LOSS_FOLDER_PATH;
      let folderId = await googleAPI.createFolderPath(folderPath, null);
      if (!folderId) { folderId = ""; }

      const activeSpreadsheetId = await googleAPI.createSpreadSheet(fileName, folderId, null);

      // 事前確保したウィンドウをスプレッドシートに遷移
      if (newWindow) {
        newWindow.location.href = googleAPI.getOpenSpradSheetUrl(activeSpreadsheetId);

        // GoogleSpreadシートウィンドウを手動で閉じられた場合
        const interval = setInterval(() => {
          if (newWindow.closed) {
            clearInterval(interval); // 監視を停止
            setInputMode("manual");
          }
        }, 500); // 500ms ごとに監視
      } else {
        console.error("GoogleSpreadシートがブロックされました");
      }

      // ヘッダー行の構築（年月）
      // 注：項目名の列は表示ロケールに応じてラベルを切り替える（データ連携時の照合も同ロケールの文言＋
      // 旧形式（日本語固定）の文言の両方を許容することで、過去にエクスポートしたファイルとの互換性を保つ）
      const spreadsheetHeader: string[] = [
        t('features.InvestmentScreening.ProfitAndLoss.label.itemHeader'),
        t('features.InvestmentScreening.ProfitAndLoss.label.currencyHeader'),
      ];
      fiscalMonthsString.forEach((month) => {
        spreadsheetHeader.push(month);
      });

      // 通貨名を取得するヘルパー関数
      const getCurrencyName = (currencyId: string): string => {
        const currency = currencyData.find(c => c.currencyId === currencyId);
        return currency?.currencyName || "";
      };

      // データ行の構築（画面レイアウトと同じ構造）
      const rows: (string | number)[][] = [];
      const monthCount = fiscalMonthsString.length;

      // 表示専用行を作成するヘルパー関数
      const createDisplayRow = (label: string): (string | number)[] => {
        const row: (string | number)[] = [label, ""];
        for (let i = 0; i < monthCount; i++) {
          row.push("-");
        }
        return row;
      };

      // 注：以下の項目名は表示ロケールに応じたラベルを使用する。
      // データ連携時の照合（後述のswitch文）は、現在ロケールの文言に加えて
      // 旧形式（日本語固定）の文言も許容し、過去にエクスポートしたファイルとの互換性を保つ。

      // ===== 1. 売上合計 =====
      rows.push(createDisplayRow(rowLabel('totalSales')));
      rows.push(createDisplayRow(`  ${rowLabel('productSales')}`));
      rows.push(createDisplayRow(`  ${rowLabel('moldEquipmentRecovery')}`));
      rows.push(createDisplayRow(`  ${rowLabel('devRecovery')}`));

      // ===== 2. 売上原価 =====
      rows.push(createDisplayRow(rowLabel('salesCost')));
      rows.push(createDisplayRow(`  ${rowLabel('materialCost')}`));

      // 外注加工費（入力）
      const outsourcingRow: (string | number)[] = [`  ${rowLabel('outsourcingCost')}`, getCurrencyName(outsourcingProcessingCostCurrencyId)];
      monthlyExpenseData.forEach((data) => outsourcingRow.push(data.outsourcingProcessingCost ?? 0));
      rows.push(outsourcingRow);

      rows.push(createDisplayRow(`  ${rowLabel('laborCost')}`));

      // 生産工場労務費（入力）
      const productionLaborRow: (string | number)[] = [`    ${rowLabel('productionLaborCost')}`, getCurrencyName(productionLaborCostCurrencyId)];
      monthlyExpenseData.forEach((data) => productionLaborRow.push(data.productionLaborCost ?? 0));
      rows.push(productionLaborRow);

      // 国内工場労務費（入力）
      const domesticLaborRow: (string | number)[] = [`    ${rowLabel('domesticLaborCost')}`, getCurrencyName(domesticLaborCostCurrencyId)];
      monthlyExpenseData.forEach((data) => domesticLaborRow.push(data.domesticLaborCost ?? 0));
      rows.push(domesticLaborRow);

      rows.push(createDisplayRow(`  ${rowLabel('factoryExpenses')}`));
      rows.push(createDisplayRow(`    ${rowLabel('productionFactoryExpenses')}`));

      // 償却費以外の工場経費（生産）（入力）
      const overseasFactoryRow: (string | number)[] = [`      ${rowLabel('overHeadCost')}`, getCurrencyName(overseasFactoryExpensesCurrencyId)];
      monthlyExpenseData.forEach((data) => overseasFactoryRow.push(data.overseasFactoryExpenses ?? 0));
      rows.push(overseasFactoryRow);

      rows.push(createDisplayRow(`      ${rowLabel('depreciation')}`));
      rows.push(createDisplayRow(`        ${rowLabel('investmentTargetEquipment')}`));

      // 工場建物（生産）（入力）
      const factoryBuildingRow: (string | number)[] = [`        ${rowLabel('factoryBuilding')}`, getCurrencyName(factoryBuildingDepreciationCurrencyId)];
      monthlyExpenseData.forEach((data) => factoryBuildingRow.push(data.factoryBuildingDepreciation ?? 0));
      rows.push(factoryBuildingRow);

      // 過去PJの投資対象設備（生産）（条件付き）
      if (projectData?.pastProjectId) {
        const pastEquipmentRow: (string | number)[] = [`        ${rowLabel('pastInvestmentTargetEquipment')}`, getCurrencyName(pastEquipmentCostCurrencyId)];
        pastEquipmentCost.forEach((item) => pastEquipmentRow.push(item?.value ?? 0));
        rows.push(pastEquipmentRow);
      }

      // その他の設備（生産）（入力）
      const otherEquipmentRow: (string | number)[] = [`        ${rowLabel('otherEquipment')}`, getCurrencyName(otherEquipmentCostCurrencyId)];
      otherEquipmentCost.forEach((item) => otherEquipmentRow.push(item?.value ?? 0));
      rows.push(otherEquipmentRow);

      rows.push(createDisplayRow(`    ${rowLabel('domesticFactoryExpenses')}`));

      // 償却費以外の工場経費（国内）（入力）
      const domesticFactoryRow: (string | number)[] = [`      ${rowLabel('overHeadCost')}`, getCurrencyName(domesticFactoryExpensesCurrencyId)];
      monthlyExpenseData.forEach((data) => domesticFactoryRow.push(data.domesticFactoryExpenses ?? 0));
      rows.push(domesticFactoryRow);

      rows.push(createDisplayRow(`      ${rowLabel('depreciation')}`));

      // 投資対象設備（国内）（入力）
      const domesticInvestmentRow: (string | number)[] = [`        ${rowLabel('investmentTargetEquipment')}`, getCurrencyName(domesticInvestmentDepreciationCurrencyId)];
      monthlyExpenseData.forEach((data) => domesticInvestmentRow.push(data.domesticInvestmentDepreciation ?? 0));
      rows.push(domesticInvestmentRow);

      // 工場建物（国内）（入力）
      const domesticFactoryBuildingRow: (string | number)[] = [`        ${rowLabel('factoryBuilding')}`, getCurrencyName(domesticFactoryBuildingDepreciationCurrencyId)];
      monthlyExpenseData.forEach((data) => domesticFactoryBuildingRow.push(data.domesticFactoryBuildingDepreciation ?? 0));
      rows.push(domesticFactoryBuildingRow);

      // その他の設備（国内）（入力）
      const domesticOtherRow: (string | number)[] = [`        ${rowLabel('otherEquipment')}`, getCurrencyName(domesticOtherDepreciationCurrencyId)];
      monthlyExpenseData.forEach((data) => domesticOtherRow.push(data.domesticOtherDepreciation ?? 0));
      rows.push(domesticOtherRow);

      rows.push(createDisplayRow(`  ${rowLabel('scrapCost')}`));

      // ===== 3. 販売費及び一般管理費 =====
      rows.push(createDisplayRow(rowLabel('sgaExpenses')));

      // 生産工場販管費（入力）
      const productionSgaRow: (string | number)[] = [`  ${rowLabel('productionSgaExpenses')}`, getCurrencyName(productionSgaExpensesCurrencyId)];
      monthlyExpenseData.forEach((data) => productionSgaRow.push(data.productionSgaExpenses ?? 0));
      rows.push(productionSgaRow);

      // 国内工場販管費（入力）
      const domesticSgaRow: (string | number)[] = [`  ${rowLabel('domesticSgaExpenses')}`, getCurrencyName(domesticSgaExpensesCurrencyId)];
      monthlyExpenseData.forEach((data) => domesticSgaRow.push(data.domesticSgaExpenses ?? 0));
      rows.push(domesticSgaRow);

      // 販社販管費（入力）
      const salesCompanySgaRow: (string | number)[] = [`  ${rowLabel('salesCompanySga')}`, getCurrencyName(salesCompanySgaCurrencyId)];
      monthlyExpenseData.forEach((data) => salesCompanySgaRow.push(data.salesCompanySga ?? 0));
      rows.push(salesCompanySgaRow);

      // 開発費（入力）
      const devCostRow: (string | number)[] = [`  ${rowLabel('devCost')}`, getCurrencyName(devCostCurrencyId)];
      devCost.forEach((item) => devCostRow.push(item?.value ?? 0));
      rows.push(devCostRow);

      // ロイヤリティ（入力）
      const royaltyRow: (string | number)[] = [`  ${rowLabel('royalty')}`, getCurrencyName(royaltyCurrencyId)];
      monthlyExpenseData.forEach((data) => royaltyRow.push(data.royalty ?? 0));
      rows.push(royaltyRow);

      // ===== 4. その他の経費 =====
      const otherCostRow: (string | number)[] = [rowLabel('otherCost'), getCurrencyName(otherCostCurrencyId)];
      otherCost.forEach((item) => otherCostRow.push(item?.value ?? 0));
      rows.push(otherCostRow);

      // ===== 5. 営業利益 =====
      rows.push(createDisplayRow(rowLabel('operatingProfit')));

      // ===== 6. 営業利益率 =====
      rows.push(createDisplayRow(rowLabel('operatingProfitRate')));

      // シート名を動的に取得（ロケール対応）
      const sheetName = await googleAPI.getFirstSheetName(activeSpreadsheetId) ?? GoogleAPI.DEFAULT_SHEET_NAME;

      // 既存データを初期化（値取得＋空白書き込みの2回を、シート全体の clear 1回に置き換え）
      await googleAPI.clear(activeSpreadsheetId, sheetName);

      // ヘッダー行（年月列）をTEXT形式に強制してから書き込む。
      // USER_ENTEREDでの書き込みは日付らしい文字列（例: 英語形式「4/2025」）を
      // Sheets側が自動で日付型に変換し、そのままセルの表示形式（numberFormat）として
      // 固定してしまうため、値を書き込む前にTEXT形式を明示的に適用して自動変換を防止する。
      const newSheetId = await googleAPI.getFirstSheetId(activeSpreadsheetId);
      if (newSheetId !== null) {
        // 前回設定した書式・入力規則を初期化（列構成変更時に古い設定が残らないようにする。値の書き込みより前に実行）
        await googleAPI.resetSheetFormatAndValidation(activeSpreadsheetId, [newSheetId]);
        await googleAPI.spreadSheetSetCellFormat(activeSpreadsheetId, [{
          sheetId: newSheetId,
          startRow: 1,
          endRow: 1,
          startCol: 3,
          endCol: 2 + fiscalMonthsString.length,
          format: { numberFormat: { type: "TEXT" } },
        }]);
      }

      // Spreadシートにデータを反映
      const data = [spreadsheetHeader, ...rows];
      await googleAPI.spreadSheetSetValue(activeSpreadsheetId, sheetName, 1, 1, data);

      // 通貨列（B列、2行目から34行目）にドロップダウンを設定
      const currencyNames = currencyData.map(c => c.currencyName);
      if (currencyNames.length > 0) {
        await googleAPI.setDataValidationDropdown(
          activeSpreadsheetId,
          0,      // シートID（最初のシート）
          1,      // 開始行インデックス（2行目 = ヘッダーの次）
          34,     // 終了行インデックス（34行目まで = 33項目分）
          1,      // 開始列インデックス（B列 = 通貨列）
          2,      // 終了列インデックス（B列のみ）
          currencyNames as string[]
        );
      }

      // A列（項目列）の幅を設定（インデント付きテキストが見やすい幅）
      await googleAPI.setColumnWidth(activeSpreadsheetId, 0, 0, 1, 200);

    } catch (error) {
      console.error("Change GoogleSpread Error.", error);
      spreadSheetWindow?.close();
      setInputMode("manual");

      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ProfitAndLoss.message.googleSpreadUnavailable'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsGoogleSpreadLoading(false);
    }
  };

  /**
   * 過去PJの投資対象設備の減価償却費を算出する
   * 過去PJの設備情報を取得し、通貨換算後に月別減価償却費を計算する
   */
  const calcPastEquipmentDepreciation = async () => {
    if (!projectData?.pastProjectId) return;

    // 紐づけ時点の承認済み振り返り日（保存済みの pastReviewDate、申請のみの場合は null）
    const pastReviewDate = projectData.pastReviewDate ?? null;

    // 過去PJの設備情報を取得
    const pastEquipmentResponse = await WebAPI.request({
      query: `
        query getEquipmentByProjectId($projectId: String, $reviewDate: String, $referencingProjectId: String) {
          getEquipmentByProjectId(projectId: $projectId, reviewDate: $reviewDate, referencingProjectId: $referencingProjectId) {
            equipmentId
            equipmentName
            unitPrice
            quantity
            amount
            currencyId
            depreciationPeriod
            depreciationStartMonth
            acceptanceMonth
            processId
          }
        }`,
      variables: { projectId: projectData.pastProjectId, ...(pastReviewDate && { reviewDate: pastReviewDate }), referencingProjectId: projectId }
    });

    if (!pastEquipmentResponse?.data?.getEquipmentByProjectId) return;

    const rawPastEquipment: EquipmentType[] = pastEquipmentResponse.data.getEquipmentByProjectId;

    // 選択通貨名を取得（ユーザーが選択した通貨、未選択時はローカル通貨）
    const targetCurrencyName = currencyData.find(
      c => c.currencyId === pastEquipmentCostCurrencyId
    )?.currencyName || selectedCurrency.name;

    // 通貨換算（各設備の通貨 → 選択通貨）
    const convertedPastEquipment = convertEquipmentData(
      rawPastEquipment,
      targetCurrencyName,
      currencyData
    );

    // 年度数の計算
    const maxYears = fiscalMonthsLabelless.length > 0
      ? Math.ceil(fiscalMonthsLabelless.length / 12)
      : 1;

    // 月別減価償却費を算出
    const depreciationByMonth = calcEquipmentDepreciationByYearMonth(
      maxYears,
      convertedPastEquipment,
      hasDecimal(targetCurrencyName),
      projectData.projectStartDate || "",
      false
    );

    // 年度番号+月のリストを生成（depreciationByMonthとのマッチング用）
    const adjustedYearMonthList = generateYearMonthList(
      projectData.projectStartDate,
      maxYears,
      true // 年度番号（1,2,3...）で生成
    );

    // EOL予定日の年月を算出
    let eolYearMonth: number | null = null;
    if (projectData?.projectEndDate) {
      const eolYear = parseInt(projectData.projectEndDate.split("-")[0], 10);
      const eolMonth = parseInt(projectData.projectEndDate.split("-")[1], 10);
      eolYearMonth = eolYear * 100 + eolMonth;
    }

    // MonthValue[]に変換（EOL予定日以降の月は0にする）
    const newPastEquipmentCost: MonthValue[] = fiscalMonthsLabelless.map((item, idx) => {
      // EOL予定日以降の月は0にする
      if (eolYearMonth !== null) {
        const currentYearMonth = item.year * 100 + item.month;
        if (currentYearMonth > eolYearMonth) {
          return { year: item.year, month: item.month, value: 0 };
        }
      }

      const adjusted = adjustedYearMonthList[idx];
      const depData = adjusted
        ? depreciationByMonth.find(d => d.year === adjusted.year && d.month === adjusted.month)
        : undefined;
      return {
        year: item.year,
        month: item.month,
        value: depData?.value.toNumber() ?? 0,
      };
    });

    setPastEquipmentCost(newPastEquipmentCost);
  };

  /**
   * 「自動算出」ボタン押下
   * 生産工場労務費単価・経費単価、C/T、販売数量から生産工場労務費・償却費以外の工場経費（生産）を算出
   */
  const handleAutoCalculate = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();

    if (isAutoCalculating) {
      return;
    }

    try {
      setIsAutoCalculating(true);

      // エラー初期化
      setErrors(clearErrors());

      // バリデーション
      let hasValidationError = false;
      // 生産工場労務費単価チェック
      if (productionFactoryLaborUnitPrice === null || productionFactoryLaborUnitPrice === undefined) {
        setErrors(prev => ({
          ...prev,
          productionFactoryLaborUnitPrice: { isError: true, errorMessage: t('features.InvestmentScreening.ProfitAndLoss.message.validationProductionLaborUnitPrice') }
        }));
        hasValidationError = true;
      }

      // 生産工場経費単価チェック
      if (productionFactoryExpenseUnitPrice === null || productionFactoryExpenseUnitPrice === undefined) {
        setErrors(prev => ({
          ...prev,
          productionFactoryExpenseUnitPrice: { isError: true, errorMessage: t('features.InvestmentScreening.ProfitAndLoss.message.validationProductionExpenseUnitPrice') }
        }));
        hasValidationError = true;
      }

      // ベースアップ率チェック
      if (projectData?.baseLaborCostIncreaseRate === null || projectData?.baseLaborCostIncreaseRate === undefined) {
        setErrors(prev => ({
          ...prev,
          baseLaborCostIncreaseRate: { isError: true, errorMessage: t('features.InvestmentScreening.ProfitAndLoss.message.validationBaseLaborCostIncreaseRate') }
        }));
        hasValidationError = true;
      }

      if (hasValidationError) {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ProfitAndLoss.message.autoCalcRequiresUnitPrice'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // PartNoデータ取得
      const partNoResponse = await WebAPI.request({
        query: `
          query getPartNo($projectId: String, $reviewDate: String) {
            getPartNo(projectId: $projectId, reviewDate: $reviewDate) {
              partNo
              displayPartNo
              quantityAndPrice
            }
          }`,
        variables: { projectId, ...(reviewDate && { reviewDate }) }
      });

      if (!partNoResponse?.data?.getPartNo) {
        throw new Error(t('features.InvestmentScreening.ProfitAndLoss.message.partNoFetchFailed'));
      }

      const partNoData: PartNo[] = partNoResponse.data.getPartNo;

      // 工程情報（サイクルタイム）取得
      const uniqueProcessData = await fetchProcess(projectId, TITLE, WebAPI, toast, reviewDate);

      if (!uniqueProcessData || uniqueProcessData.length === 0) {
        throw new Error(t('features.InvestmentScreening.ProfitAndLoss.message.processFetchFailed'));
      }

      // プロジェクト開始日を取得
      const startDate = projectData?.projectStartDate || "";

      if (!startDate) {
        throw new Error(t('features.InvestmentScreening.ProfitAndLoss.message.projectStartDateNotSet'));
      }

      // 年月リストを取得（fiscalMonthsLabelless相当）
      const yearMonthList = fiscalMonthsLabelless.map(item => ({
        year: item.year,
        month: item.month
      }));

      // 月別のC/T合計を計算
      const totalCycleTimeByMonth: Decimal[] = calcTotalCycleTimeByMonth(uniqueProcessData, yearMonthList);

      // PartNo別月別数量を取得
      const quantitiesByPartNoMonth = getQuantitiesByPartNoMonth(partNoData, startDate);

      // 月別販売数量合計を計算
      const totalSalesQuantityByMonth: Decimal[] = calcTotalQuantityByMonth(
        quantitiesByPartNoMonth,
        yearMonthList.length
      );

      // 生産工場労務費の通貨情報を取得
      const laborCostCurrencyName = currencyData?.find(
        c => c.currencyId === productionLaborCostCurrencyId
      )?.currencyName;
      const laborCostDecimalCurrency = laborCostCurrencyName !== CurrencyNameJPY;

      // 生産工場労務費を計算
      const laborCostByMonth = calcCycleTimeBasedCost(
        totalCycleTimeByMonth,
        new Decimal(productionFactoryLaborUnitPrice ?? 0),
        laborCostCurrencyName,
        laborCostCurrencyName, // 同じ通貨（変換なし）
        currencyData,
        totalSalesQuantityByMonth,
        laborCostDecimalCurrency,
        "monthly",
        "calcProductionLaborCost"
      );

      // ベースアップ率を適用
      applyBaseUpRateToLaborCost(laborCostByMonth, projectData?.baseLaborCostIncreaseRate, startDate);

      // 海外工場経費の通貨情報を取得
      const overseasExpensesCurrencyName = currencyData?.find(
        c => c.currencyId === overseasFactoryExpensesCurrencyId
      )?.currencyName;
      const overseasExpensesDecimalCurrency = overseasExpensesCurrencyName !== CurrencyNameJPY;

      // 海外工場経費を計算
      const overseasExpensesByMonth = calcCycleTimeBasedCost(
        totalCycleTimeByMonth,
        new Decimal(productionFactoryExpenseUnitPrice ?? 0),
        overseasExpensesCurrencyName,
        overseasExpensesCurrencyName, // 同じ通貨（変換なし）
        currencyData,
        totalSalesQuantityByMonth,
        overseasExpensesDecimalCurrency,
        "monthly",
        "calcOverseasFactoryExpenses"
      );

      // monthlyExpenseData に計算結果を反映
      setMonthlyExpenseData(prev => {
        return prev.map((data, idx) => ({
          ...data,
          productionLaborCost: laborCostByMonth[idx]?.toNumber() ?? 0,
          overseasFactoryExpenses: overseasExpensesByMonth[idx]?.toNumber() ?? 0,
        }));
      });

      // 過去PJの投資対象設備の減価償却費を算出
      if (projectData?.pastProjectId) {
        try {
          await calcPastEquipmentDepreciation();
        } catch (err) {
          console.error("過去PJ設備減価償却費の算出エラー:", err);
          toast({
            title: TITLE,
            description: t('features.InvestmentScreening.ProfitAndLoss.message.pastEquipmentCalcFailed'),
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
      }

      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ProfitAndLoss.message.autoCalcSuccess'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

    } catch (err: any) {
      console.error("handleAutoCalculate error:", err);
      toast({
        title: TITLE,
        description: err.message || t('features.InvestmentScreening.ProfitAndLoss.message.autoCalcFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsAutoCalculating(false);
    }
  };

  /**
   * 「データ連携」ボタン押下
   * スプレッドシートからデータを読み込み、画面に反映する
   */
  const handleSyncData = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();

    if (isSyncDataLoading) {
      return;
    }

    try {
      setIsSyncDataLoading(true);

      // Googleアカウント認証
      if (!await googleAPI.signIn()) {
        throw new Error("signIn error");
      }

      const fileName = `ProfitAndLoss_${projectId}`;
      const folderPath = PROFIT_AND_LOSS_FOLDER_PATH;
      let folderId = await googleAPI.createFolderPath(folderPath, null);
      if (!folderId) { folderId = ""; }
      const activeSpreadsheetId = await googleAPI.createSpreadSheet(fileName, folderId, null);

      if (!activeSpreadsheetId) {
        console.error("Spreadsheet ID is missing.");
        return;
      }

      // シート名を動的に取得（ロケール対応）
      const sheetName = await googleAPI.getFirstSheetName(activeSpreadsheetId) ?? GoogleAPI.DEFAULT_SHEET_NAME;

      // スプレッドシートから値を取得
      const result = await googleAPI.spreadSheetGetValues(activeSpreadsheetId, sheetName);
      const { values } = result;

      if (!values || values.length < 2) {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ProfitAndLoss.message.spreadsheetEmpty'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // ヘッダー行の検証（列数のみ。テキスト内容は言語によって表示形式が異なる
      // 〔例: 日本語「2025年4月」/ 英語「4/2025」〕ため文言そのものでの突き合わせは行わない）
      const headerRow = values[0];
      const yearMonthHeaders = headerRow.slice(2); // 「項目」と「通貨」の列を除く

      if (yearMonthHeaders.length !== fiscalMonthsString.length) {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ProfitAndLoss.message.headerInvalid', { ym: yearMonthHeaders.join(', ') }),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // データ連携のたびにヘッダー（年月）は現在の選択言語のフォーマットで無条件に上書きする。
      // 言語切替前に作成/連携されたシートでも、これで毎回ヘッダーが最新言語に修復される。
      // ※ USER_ENTEREDでの書き込みは日付らしい文字列（例: 英語形式「4/2025」）を
      //   Sheets側が自動で日付型に変換し、そのままセルの表示形式（numberFormat）として
      //   固定してしまう。そのため値を書き込む前にヘッダー範囲をTEXT形式に強制し、
      //   自動変換・書式固定を防止する。
      const headerSheetId = await googleAPI.getFirstSheetId(activeSpreadsheetId);
      if (headerSheetId !== null) {
        await googleAPI.spreadSheetSetCellFormat(activeSpreadsheetId, [{
          sheetId: headerSheetId,
          startRow: 1,
          endRow: 1,
          startCol: 3,
          endCol: 2 + fiscalMonthsString.length,
          format: { numberFormat: { type: "TEXT" } },
        }]);
      }
      await googleAPI.spreadSheetSetValue(activeSpreadsheetId, sheetName, 1, 3, [fiscalMonthsString]);

      // 通貨名からIDを取得するヘルパー関数
      const getCurrencyId = (currencyName: string): string => {
        const currency = currencyData.find(c => c.currencyName === currencyName);
        return currency?.currencyId || "";
      };

      // EOL予定日以降のデータを0にする処理用フラグ
      let eolWarningShown = false;

      // 各行のデータを処理
      for (let rowIndex = 1; rowIndex < values.length; rowIndex++) {
        const row = values[rowIndex];
        const itemName = row[0];
        const currencyName = row[1] || "";
        const dataValues = row.slice(2).map((v: string) => {
          // カンマを除去してから数値に変換（例: "105,000" → 105000）
          const cleanedValue = String(v).replace(/,/g, '');
          return parseFloat(cleanedValue) || 0;
        });

        // インデントを除去した項目名を取得
        const trimmedItemName = itemName.trim();

        // 親項目のコンテキストを追跡するため、行番号ベースで判定
        // インデントレベルで生産工場/国内工場を区別
        const indentLevel = itemName.length - trimmedItemName.length;

        // 入力項目の処理（表示専用項目はスキップ）
        // 注：判定は現在ロケールの項目名に加え、旧形式（日本語固定）の項目名も許容し、
        // 過去にエクスポートしたファイルとの互換性を保つ
        switch (trimmedItemName) {
          // 外注加工費
          case "外注加工費":
          case rowLabel('outsourcingCost'):
            setOutsourcingProcessingCostCurrencyId(getCurrencyId(currencyName));
            setMonthlyExpenseData((prev) => {
              return prev.map((item, idx) => {
                const afterEol = isAfterEOL(idx);
                if (afterEol && !eolWarningShown) eolWarningShown = true;
                return { ...item, outsourcingProcessingCost: afterEol ? 0 : (dataValues[idx] ?? 0) };
              });
            });
            break;

          // 生産工場労務費
          case "生産工場労務費":
          case rowLabel('productionLaborCost'):
            setProductionLaborCostCurrencyId(getCurrencyId(currencyName));
            setMonthlyExpenseData((prev) => {
              return prev.map((item, idx) => {
                const afterEol = isAfterEOL(idx);
                if (afterEol && !eolWarningShown) eolWarningShown = true;
                return { ...item, productionLaborCost: afterEol ? 0 : (dataValues[idx] ?? 0) };
              });
            });
            break;

          // 国内工場労務費
          case "国内工場労務費":
          case rowLabel('domesticLaborCost'):
            setDomesticLaborCostCurrencyId(getCurrencyId(currencyName));
            setMonthlyExpenseData((prev) => {
              return prev.map((item, idx) => {
                const afterEol = isAfterEOL(idx);
                if (afterEol && !eolWarningShown) eolWarningShown = true;
                return { ...item, domesticLaborCost: afterEol ? 0 : (dataValues[idx] ?? 0) };
              });
            });
            break;

          // 償却費以外の工場経費（生産/国内を区別）
          case "償却費以外の工場経費":
          case "償却費以外の工場経費（生産）":
          case "償却費以外の工場経費（国内）":
          case "国内工場経費":
          case "海外工場経費":
          case rowLabel('overHeadCost'):
            // 注：rowLabel('overHeadCost') は生産/国内の両方で同じ文言（現在ロケールの表示名）になるため、
            // 項目名の文字列だけでは判定できない。行番号（出現順）を主判定とし、
            // 国内/生産が明示された旧形式の固有文言がある場合のみそれを優先する。
            if (trimmedItemName === "償却費以外の工場経費（国内）" || trimmedItemName === "国内工場経費") {
              // 国内工場経費の下（旧形式の明示文言）
              setDomesticFactoryExpensesCurrencyId(getCurrencyId(currencyName));
              setMonthlyExpenseData((prev) => {
                return prev.map((item, idx) => {
                  const afterEol = isAfterEOL(idx);
                  if (afterEol && !eolWarningShown) eolWarningShown = true;
                  return { ...item, domesticFactoryExpenses: afterEol ? 0 : (dataValues[idx] ?? 0) };
                });
              });
            } else if (rowIndex < 18 || trimmedItemName === "償却費以外の工場経費（生産）" || trimmedItemName === "海外工場経費") {
              // 生産工場経費の下（先に出現。現在ロケール文言・旧形式ともに行番号 or 明示文言で判定）
              setOverseasFactoryExpensesCurrencyId(getCurrencyId(currencyName));
              setMonthlyExpenseData((prev) => {
                return prev.map((item, idx) => {
                  const afterEol = isAfterEOL(idx);
                  if (afterEol && !eolWarningShown) eolWarningShown = true;
                  return { ...item, overseasFactoryExpenses: afterEol ? 0 : (dataValues[idx] ?? 0) };
                });
              });
            } else {
              // 国内工場経費の下（後に出現）
              setDomesticFactoryExpensesCurrencyId(getCurrencyId(currencyName));
              setMonthlyExpenseData((prev) => {
                return prev.map((item, idx) => {
                  const afterEol = isAfterEOL(idx);
                  if (afterEol && !eolWarningShown) eolWarningShown = true;
                  return { ...item, domesticFactoryExpenses: afterEol ? 0 : (dataValues[idx] ?? 0) };
                });
              });
            }
            break;

          // 投資対象設備（国内のみ入力可能）
          case "投資対象設備":
          case rowLabel('investmentTargetEquipment'):
            // 国内工場経費の下の投資対象設備（インデント8文字）
            if (indentLevel === 8 && rowIndex > 17) {
              setDomesticInvestmentDepreciationCurrencyId(getCurrencyId(currencyName));
              setMonthlyExpenseData((prev) => {
                return prev.map((item, idx) => {
                  const afterEol = isAfterEOL(idx);
                  if (afterEol && !eolWarningShown) eolWarningShown = true;
                  return { ...item, domesticInvestmentDepreciation: afterEol ? 0 : (dataValues[idx] ?? 0) };
                });
              });
            }
            break;

          // 工場建物（生産/国内を行番号で区別）
          case "工場建物":
          case "工場建物の減価償却費":
          case rowLabel('factoryBuilding'):
            if (rowIndex < 18 || trimmedItemName === "工場建物の減価償却費") {
              // 生産工場経費の下
              setFactoryBuildingDepreciationCurrencyId(getCurrencyId(currencyName));
              setMonthlyExpenseData((prev) => {
                return prev.map((item, idx) => {
                  const afterEol = isAfterEOL(idx);
                  if (afterEol && !eolWarningShown) eolWarningShown = true;
                  return { ...item, factoryBuildingDepreciation: afterEol ? 0 : (dataValues[idx] ?? 0) };
                });
              });
            } else {
              // 国内工場経費の下
              setDomesticFactoryBuildingDepreciationCurrencyId(getCurrencyId(currencyName));
              setMonthlyExpenseData((prev) => {
                return prev.map((item, idx) => {
                  const afterEol = isAfterEOL(idx);
                  if (afterEol && !eolWarningShown) eolWarningShown = true;
                  return { ...item, domesticFactoryBuildingDepreciation: afterEol ? 0 : (dataValues[idx] ?? 0) };
                });
              });
            }
            break;

          // 過去PJの投資対象設備（生産）
          case "過去PJの投資対象設備":
          case rowLabel('pastInvestmentTargetEquipment'):
            setPastEquipmentCost((prev) => {
              return prev.map((item, idx) => {
                const afterEol = isAfterEOL(idx);
                if (afterEol && !eolWarningShown) eolWarningShown = true;
                return { ...item, value: afterEol ? 0 : (dataValues[idx] ?? 0) };
              });
            });
            break;

          // その他の設備（生産/国内を行番号で区別）
          case "その他の設備":
          case "投資対象設備以外の減価償却費":
          case rowLabel('otherEquipment'):
            if (rowIndex < 18 || trimmedItemName === "投資対象設備以外の減価償却費") {
              // 生産工場経費の下
              setOtherEquipmentCostCurrencyId(getCurrencyId(currencyName));
              setOtherEquipmentCost((prev) => {
                return prev.map((item, idx) => {
                  const afterEol = isAfterEOL(idx);
                  if (afterEol && !eolWarningShown) eolWarningShown = true;
                  return { ...item, value: afterEol ? 0 : (dataValues[idx] ?? 0) };
                });
              });
            } else {
              // 国内工場経費の下
              setDomesticOtherDepreciationCurrencyId(getCurrencyId(currencyName));
              setMonthlyExpenseData((prev) => {
                return prev.map((item, idx) => {
                  const afterEol = isAfterEOL(idx);
                  if (afterEol && !eolWarningShown) eolWarningShown = true;
                  return { ...item, domesticOtherDepreciation: afterEol ? 0 : (dataValues[idx] ?? 0) };
                });
              });
            }
            break;

          // 生産工場販管費
          case "生産工場販管費":
          case rowLabel('productionSgaExpenses'):
            setProductionSgaExpensesCurrencyId(getCurrencyId(currencyName));
            setMonthlyExpenseData((prev) => {
              return prev.map((item, idx) => {
                const afterEol = isAfterEOL(idx);
                if (afterEol && !eolWarningShown) eolWarningShown = true;
                return { ...item, productionSgaExpenses: afterEol ? 0 : (dataValues[idx] ?? 0) };
              });
            });
            break;

          // 国内工場販管費
          case "国内工場販管費":
          case rowLabel('domesticSgaExpenses'):
            setDomesticSgaExpensesCurrencyId(getCurrencyId(currencyName));
            setMonthlyExpenseData((prev) => {
              return prev.map((item, idx) => {
                const afterEol = isAfterEOL(idx);
                if (afterEol && !eolWarningShown) eolWarningShown = true;
                return { ...item, domesticSgaExpenses: afterEol ? 0 : (dataValues[idx] ?? 0) };
              });
            });
            break;

          // 販社販管費
          case "販社販管費":
          case rowLabel('salesCompanySga'):
            setSalesCompanySgaCurrencyId(getCurrencyId(currencyName));
            setMonthlyExpenseData((prev) => {
              return prev.map((item, idx) => {
                const afterEol = isAfterEOL(idx);
                if (afterEol && !eolWarningShown) eolWarningShown = true;
                return { ...item, salesCompanySga: afterEol ? 0 : (dataValues[idx] ?? 0) };
              });
            });
            break;

          // 開発費
          case "開発費":
          case rowLabel('devCost'):
            setDevCostCurrencyId(getCurrencyId(currencyName));
            setDevCost((prev) => {
              return prev.map((item, idx) => {
                const afterEol = isAfterEOL(idx);
                if (afterEol && !eolWarningShown) eolWarningShown = true;
                return { ...item, value: afterEol ? 0 : (dataValues[idx] ?? 0) };
              });
            });
            break;

          // ロイヤリティ
          case "ロイヤリティ":
          case rowLabel('royalty'):
            setRoyaltyCurrencyId(getCurrencyId(currencyName));
            setMonthlyExpenseData((prev) => {
              return prev.map((item, idx) => {
                const afterEol = isAfterEOL(idx);
                if (afterEol && !eolWarningShown) eolWarningShown = true;
                return { ...item, royalty: afterEol ? 0 : (dataValues[idx] ?? 0) };
              });
            });
            break;

          // その他の経費
          case "その他経費":
          case "その他の経費":
          case rowLabel('otherCost'):
            setOtherCostCurrencyId(getCurrencyId(currencyName));
            setOtherCost((prev) => {
              return prev.map((item, idx) => {
                const afterEol = isAfterEOL(idx);
                if (afterEol && !eolWarningShown) eolWarningShown = true;
                return { ...item, value: afterEol ? 0 : (dataValues[idx] ?? 0) };
              });
            });
            break;
        }
      }

      // EOL予定日以降のデータがあった場合は警告を表示
      if (eolWarningShown && projectData?.projectEndDate) {
        const eolYear = parseInt(projectData.projectEndDate.split("-")[0], 10);
        const eolMonth = parseInt(projectData.projectEndDate.split("-")[1], 10);
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ProfitAndLoss.message.eolDateWarning', { endDateFieldName: t('features.InvestmentScreening.BasicInfo.field.projectEndDate'), year: eolYear, month: eolMonth }),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }

      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ProfitAndLoss.message.dataSyncSuccess'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      console.error("データ連携エラー:", error);
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ProfitAndLoss.message.dataSyncError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSyncDataLoading(false);
    }
  };

  /**
   * プロジェクト情報更新
   * @param name プロパティ名
   * @param value 値
   */
  const handleProjectDataChange = (name: keyof ProjectWithRdsData, value: any) => {
    setProjectData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  /** 部署確認ボタン押下時 */
  const handleDepartmentConfirm: React.MouseEventHandler<HTMLButtonElement> = async (event) => {

    event.currentTarget.blur();

    // 画面間入力チェック
    if (!isRequiredDataEntered()) { return; }

    // 入力値チェック
    if (!validInputData()) { return; }

    // どの部署からの確認かを保持
    const { name } = event.target as HTMLButtonElement;
    setConfirmSource(name);

    // 選択されたスパンの比率を適用
    onSpanConfirmOpen();
  };

  // -----------------------------
  // ロジック
  // -----------------------------
  /** フォーマッタ */
  const floorAndFormat = (value?: number) => {
    if (!value) return "0";
    return formatNumber(value, "WithDecimal");
  };

  const isRequiredDataEntered = () => {
    // 基本情報
    if (!projectId || projectId === "") {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ProfitAndLoss.message.registerFromBasicInfo'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
    return true;
  }

  /** P&Lファイル読込（複数ファイル対応） */
  const fileParserMultiple = async (
    fileContents: { fileName: string; content: string }[]
  ) => {
    try {
      const newFiscalDataList: { fileName: string; fiscalData: any; plTitle: string; currency: string }[] = [];
      const newDateRangeLabelList: { fileName: string; label: string }[] = [];

      for (const fileContent of fileContents) {
        const result = await fileParserSingle(fileContent.content, fileContent.fileName);
        if (result) {
          newFiscalDataList.push({ fileName: fileContent.fileName, fiscalData: result.fiscalData, plTitle: result.plTitle, currency: result.currency });
          newDateRangeLabelList.push({ fileName: fileContent.fileName, label: result.dateRangeLabel });
        }
      }

      setFiscalDataList(newFiscalDataList);
      setDateRangeLabelList(newDateRangeLabelList);

    } catch (error) {
      setFiscalDataList([]);
      setDateRangeLabelList([]);

      console.error("Error parsing Excel files:", error);
      toast({
        title: 'P&L(Profit Center)',
        description: t('features.InvestmentScreening.ProfitAndLoss.message.plFileReadFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  /** P&Lファイル読込（単一ファイル処理・結果を返す） */
  const fileParserSingle = async (
    fileData: string,
    fileName: string
  ): Promise<{ fiscalData: any; dateRangeLabel: string; plTitle: string; currency: string } | null> => {
    try {
      // P&Lファイル読込
      const { fiscalSums, monthlyData, valueDescription, fiscalYears, plTitle, ...restValues } = parsePAndLFile(fileData);
      let lcValue = restValues.lcValue;

      // FactoryOverhead(Depreciation除く) を追加計算
      Object.keys(fiscalSums).forEach((year) => {
        const y = Number(year);
        const outside = fiscalSums[y]["OutsideProcess"] || 0;
        const labor = fiscalSums[y]["Labor"] || 0;
        const fo = fiscalSums[y]["FactoryOverhead"] || 0;
        const dep = fiscalSums[y]["Depreciation"] || 0;
        const foexdep = fiscalSums[y]["FactoryOverheadExceptDepr"] = fo - dep;
        const ns = fiscalSums[y]["NetSales"] || 0;
        const sga = fiscalSums[y]["FactorySGA"] || 0;
        if (ns !== 0) {
          fiscalSums[y]["OutSideToSalesRatio"] = outside / ns;
          fiscalSums[y]["LaborToSalesRatio"] = labor / ns;
          fiscalSums[y]["OverheadToSalesRatio"] = fo / ns;
          fiscalSums[y]["DeprToSalesRatio"] = dep / ns;
          fiscalSums[y]["OverheadExceptDeprToSalesRatio"] = foexdep / ns;
          fiscalSums[y]["FactorySgaToSalesRatio"] = sga / ns;
        } else {
          fiscalSums[y]["OutSideToSalesRatio"] = 0;
          fiscalSums[y]["LaborToSalesRatio"] = 0;
          fiscalSums[y]["OverheadToSalesRatio"] = 0;
          fiscalSums[y]["DeprToSalesRatio"] = 0;
          fiscalSums[y]["OverheadExceptDeprToSalesRatio"] = 0;
          fiscalSums[y]["FactorySgaToSalesRatio"] = 0;
        }
      });

      // YYYYMM 毎のデータをソート
      const monthCols = Object.keys(monthlyData).sort();

      let rangeLabel = "";
      if (monthCols.length > 0) {
        const first = monthCols[0];
        const last = monthCols[monthCols.length - 1];
        const formatYyyyMm = (yyyymm: string) => {
          const y = yyyymm.slice(0, 4);
          const m = String(Number(yyyymm.slice(4, 6)));
          return t('common.dateFormat.yearMonth', { year: y, month: m });
        };
        rangeLabel = `${formatYyyyMm(first)}～${formatYyyyMm(last)}`;
      }

      console.log(`年度別集計結果(${fileName}):`, fiscalSums);

      return {
        fiscalData: fiscalSums,
        dateRangeLabel: rangeLabel,
        plTitle: plTitle,
        currency: lcValue,
      };
    } catch (error) {
      console.error(`Error parsing Excel file (${fileName}):`, error);
      return null;
    }
  }

  /**
   * 入力値検証（バリデーションチェック）
   * @returns true：入力正常、false：入力エラー
   */
  const validInputData = (): boolean => {
    const requiredMessage = t('features.InvestmentScreening.ProfitAndLoss.validation.requiredSuffix');
    let isError = false;

    setErrors(clearErrors());

    // 簡易モード時は費用画面の入力をスキップ可能としているため、バリデーションを全てスキップ
    if (isSimpleMode) {
      return true;
    }

    if ((projectData?.baseLaborCostIncreaseRate ?? null) === null) {
      changeErrors("baseLaborCostIncreaseRate", `${t('features.InvestmentScreening.ProfitAndLoss.label.baseLaborCostIncreaseRate')}${requiredMessage}`)
      isError = true;
    }

    // P&Lファイルが読み込まれている場合のみチェック
    if (fiscalDataList.length > 0) {
      // 生産工場労務費単価
      if (productionFactoryLaborUnitPrice === null) {
        changeErrors("productionFactoryLaborUnitPrice", `${t('features.InvestmentScreening.ProfitAndLoss.label.productionFactoryLaborUnitPrice')}${requiredMessage}`)
        isError = true;
      }

      // 生産工場経費単価
      if (productionFactoryExpenseUnitPrice === null) {
        changeErrors("productionFactoryExpenseUnitPrice", `${t('features.InvestmentScreening.ProfitAndLoss.label.productionFactoryExpenseUnitPrice')}${requiredMessage}`)
        isError = true;
      }
    }

    // 費用項目の通貨チェック（まとめて1つのエラーとして表示）
    const hasCurrencyError =
      !outsourcingProcessingCostCurrencyId ||
      !factoryBuildingDepreciationCurrencyId ||
      !otherEquipmentCostCurrencyId ||
      !domesticLaborCostCurrencyId ||
      !domesticSgaExpensesCurrencyId ||
      !productionSgaExpensesCurrencyId ||
      !domesticFactoryExpensesCurrencyId ||
      !domesticInvestmentDepreciationCurrencyId ||
      !domesticFactoryBuildingDepreciationCurrencyId ||
      !domesticOtherDepreciationCurrencyId ||
      !salesCompanySgaCurrencyId ||
      !devCostCurrencyId ||
      !royaltyCurrencyId ||
      !otherCostCurrencyId ||
      !productionLaborCostCurrencyId ||
      !overseasFactoryExpensesCurrencyId;

    if (hasCurrencyError) {
      changeErrors("expenseCurrency", t('features.InvestmentScreening.ProfitAndLoss.validation.currencyRequired'))
      isError = true;
    }

    return !isError;
  }

  /**
   * エラー情報を設定する。
   * @param name エラーとなったプロパティ名
   * @param errorMessage エラーメッセージ
   */
  const changeErrors = (name: string, errorMessage: string) => {
    setErrors((prevState) => ({
      ...prevState,
      [name]: { isError: true, errorMessage: `${errorMessage}` },
    }));
  };

  /**
   * 配列の値を変更する。
   * @param setArray 配列値設定処理
   * @param index 変更対象配列インデックス
   * @param newValue 値
   */
  const handleChangeArrayValue = <T,>(
    setArray: React.Dispatch<React.SetStateAction<T[]>>,
    index: number,
    newValue: T
  ) => {
    setArray((prev) => {
      // 範囲外チェック
      if (index < 0 || index >= prev.length) {
        return prev;
      }
      const newArray = [...prev];
      newArray[index] = newValue;
      return newArray;
    });
  };

  /**
   * 指定された月がEOL予定日を超えているかを判定
   * @param index fiscalMonthsString / fiscalMonthsLabelless に対応するインデックス
   * @returns EOL予定日を超えている場合はtrue
   */
  const isAfterEOL = (index: number): boolean => {
    const projectEndDate = projectData?.projectEndDate;

    // EOL予定日が設定されていない場合は非活性にしない
    if (!projectEndDate) {
      return false;
    }

    // EOL予定日の年月を取得
    const eolYear = parseInt(projectEndDate.split("-")[0], 10);
    const eolMonth = parseInt(projectEndDate.split("-")[1], 10);

    // インデックスに対応する年月を取得（表示ロケールに依存しないラベルなしデータを使用）
    const target = fiscalMonthsLabelless[index];

    if (!target) {
      return false;
    }

    const { year: currentYear, month: currentMonth } = target;

    // 年月を比較（YYYYMM形式で比較）
    const currentYearMonth = currentYear * 100 + currentMonth;
    const eolYearMonth = eolYear * 100 + eolMonth;

    return currentYearMonth > eolYearMonth;
  };

  // -----------------------------
  // 画面レイアウト
  // -----------------------------
  return (
    <Box
      position="fixed"
      top="10vh"
      ml="2vw"
      mr="2vw"
      left="250px"
      width="calc(96vw - 250px)"
      height="85vh"
      p={6}
      overflowY="auto"
      bg="white"
    >
      <fieldset disabled={!pagePermissions?.isEditable}>
        <Text mb="10px" color="blue">{t('features.InvestmentScreening.ProfitAndLoss.label.projectName')} :
          {projectData?.revisedOpportunityNameByRevision ? projectData.revisedOpportunityNameByRevision : projectData?.opportunityName}
        </Text>

        {isSimpleMode && (
          <Box
            maxW="79.5vw"
            mx="auto"
            borderWidth="1px"
            borderRadius="md"
            boxShadow="md"
            p={6}
            mb={6}
            bg="gray.50"
          >
            <Text color="gray.600" fontSize="md" textAlign="center">
              {t('features.InvestmentScreening.ProfitAndLoss.message.simpleModeNote')}
            </Text>
          </Box>
        )}

        {!isSimpleMode && (<>
          <Flex align="center" mb={2}>
            <Text fontSize="lg" fontWeight="bold">
              {t('features.InvestmentScreening.ProfitAndLoss.label.profitAndLossRef')}
            </Text>
          </Flex>

          <Box mt={2} p={2} borderWidth="1px" borderRadius="md">
            <Flex align="center">
              <HStack>
                <Text
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "500px",
                  }}
                >{profitAndLossFileName || ""}</Text>
                <Button
                  colorScheme="blue"
                  size="sm"
                  mr="5px"
                  onClick={onOpenPLSUploadModal}
                >
                  {t('features.InvestmentScreening.ProfitAndLoss.label.plsUpload')}
                </Button>
              </HStack>
              <HelpIcon>
                {t('features.InvestmentScreening.ProfitAndLoss.help.plsUploadDesc1')}<br />
                {t('features.InvestmentScreening.ProfitAndLoss.help.plsUploadDesc2')}<br />
                {t('features.InvestmentScreening.ProfitAndLoss.help.plsUploadDesc3')}
              </HelpIcon>
            </Flex>
          </Box>

          {/* fiscalData の表示用テーブル */}
          <Box mt={6}>
            <FormControl mt={2} isInvalid={errors.productionFactoryLaborUnitPrice.isError}>
              <Flex align="center">
                <Text fontWeight="bold" mr={4} whiteSpace="nowrap" w={WIDTH_RATE_VALUE}>
                  {t('features.InvestmentScreening.ProfitAndLoss.label.productionFactoryLaborUnitPrice')}
                </Text>
                <CustomNumberInput
                  value={productionFactoryLaborUnitPrice ?? null}
                  onChange={(value) => { setProductionFactoryLaborUnitPrice(value) }}
                  min={0}
                  max={10000}
                  w={150}
                  decimalPlaces={4}
                  isInvalid={errors.productionFactoryLaborUnitPrice.isError}
                />
                <Select
                  size="sm"
                  w="90px"
                  ml={2}
                  value={productionLaborCostCurrencyId}
                  onChange={(e) => setProductionLaborCostCurrencyId(e.target.value)}
                >
                  <option value="">{t('features.InvestmentScreening.ProfitAndLoss.label.select')}</option>
                  {currencyData.map((currency) => (
                    <option key={currency.currencyId} value={currency.currencyId}>
                      {currency.currencyName}
                    </option>
                  ))}
                </Select>
                <Text ml={2}>/ min</Text>
              </Flex>
              <FormErrorMessage ml={WIDTH_RATE_VALUE + 10}>{errors.productionFactoryLaborUnitPrice.errorMessage}</FormErrorMessage>
            </FormControl>

            <FormControl mt={2} mb={2} isInvalid={errors.productionFactoryExpenseUnitPrice.isError}>
              <Flex align="center">
                <Text fontWeight="bold" mr={4} whiteSpace="nowrap" w={WIDTH_RATE_VALUE}>
                  {t('features.InvestmentScreening.ProfitAndLoss.label.productionFactoryExpenseUnitPrice')}
                </Text>
                <CustomNumberInput
                  value={productionFactoryExpenseUnitPrice ?? null}
                  onChange={(value) => { setProductionFactoryExpenseUnitPrice(value) }}
                  min={0}
                  max={10000}
                  w={150}
                  decimalPlaces={4}
                  isInvalid={errors.productionFactoryExpenseUnitPrice.isError}
                />
                <Select
                  size="sm"
                  w="90px"
                  ml={2}
                  value={overseasFactoryExpensesCurrencyId}
                  onChange={(e) => setOverseasFactoryExpensesCurrencyId(e.target.value)}
                >
                  <option value="">{t('features.InvestmentScreening.ProfitAndLoss.label.select')}</option>
                  {currencyData.map((currency) => (
                    <option key={currency.currencyId} value={currency.currencyId}>
                      {currency.currencyName}
                    </option>
                  ))}
                </Select>
                <Text ml={2}>/ min</Text>
              </Flex>
              <FormErrorMessage ml={WIDTH_RATE_VALUE + 10}>{errors.productionFactoryExpenseUnitPrice.errorMessage}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.baseLaborCostIncreaseRate.isError}>
              <Flex align="center">
                <Text fontWeight="bold" mr={4} whiteSpace="nowrap" w={WIDTH_RATE_VALUE}>
                  {t('features.InvestmentScreening.ProfitAndLoss.label.baseLaborCostIncreaseRate')}
                </Text>
                <CustomNumberInput
                  value={projectData?.baseLaborCostIncreaseRate ?? null}
                  onChange={(value) => { handleProjectDataChange("baseLaborCostIncreaseRate", value) }}
                  min={0}
                  max={100}
                  w={75}
                  decimalPlaces={2}
                  isInvalid={errors.baseLaborCostIncreaseRate.isError}
                />
                <Text ml={2}>%</Text>
              </Flex>
              <FormErrorMessage ml={WIDTH_RATE_VALUE + 10}>{errors.baseLaborCostIncreaseRate.errorMessage}</FormErrorMessage>
            </FormControl>

            <Box mt={4} mb={4}>
              <Flex align="center">
                <Button
                  colorScheme="blue"
                  mr="5px"
                  size="sm"
                  onClick={onOpenReportUploadModal}
                >
                  {t('features.InvestmentScreening.ProfitAndLoss.label.reportUpload')}
                </Button>
                <HelpIcon>
                  <Text mb={2}>
                    {t('features.InvestmentScreening.ProfitAndLoss.help.reportUploadDesc1')}<br />
                    {t('features.InvestmentScreening.ProfitAndLoss.help.reportUploadDesc2')}
                  </Text>
                  <Text>
                    {t('features.InvestmentScreening.ProfitAndLoss.help.reportUploadDesc3')}<br />
                    {t('features.InvestmentScreening.ProfitAndLoss.help.reportUploadDesc4')}<br />
                    {t('features.InvestmentScreening.ProfitAndLoss.help.reportUploadDesc5')}<br />
                    {t('features.InvestmentScreening.ProfitAndLoss.help.reportUploadDesc6')}
                  </Text>
                </HelpIcon>
              </Flex>
            </Box>

            {/* 費用入力テーブル */}
            <Box mt={6} mb={4} overflowX="auto">
              <HStack spacing={4} mb={2}>
                <Text fontSize="lg" fontWeight="bold">
                  {t('features.InvestmentScreening.ProfitAndLoss.label.expenseInput')}
                </Text>
                <HStack spacing={4}>
                  <ButtonGroup isAttached>
                    <ReadOnlyExemptButton
                      colorScheme={inputMode === "manual" ? "green" : "gray"}
                      isLoading={isGoogleSpreadLoading || isSyncDataLoading}
                      onClick={(e) => {
                        setInputMode("manual");
                        handleManualClick(e);
                      }}
                    >
                      {t('features.InvestmentScreening.ProfitAndLoss.label.manualInput')}
                    </ReadOnlyExemptButton>
                    <ReadOnlyExemptButton
                      colorScheme={inputMode === "google" ? "green" : "gray"}
                      isLoading={isGoogleSpreadLoading || isSyncDataLoading}
                      onClick={(e) => {
                        setInputMode("google");
                        handleGoogleSpreadClick(e);
                      }}
                    >
                      GoogleSpread
                    </ReadOnlyExemptButton>
                  </ButtonGroup>
                </HStack>
                <Button
                  colorScheme="blue"
                  onClick={(e) => handleSyncData(e)}
                  disabled={inputMode === "manual"}
                  isLoading={isSyncDataLoading}
                >
                  {t('features.InvestmentScreening.ProfitAndLoss.label.dataLink')}
                </Button>
                <Flex align="center">
                  <Button
                    colorScheme="blue"
                    mr="5px"
                    onClick={(e) => handleAutoCalculate(e)}
                    isLoading={isAutoCalculating}
                    isDisabled={inputMode === "google"}
                  >
                    {t('features.InvestmentScreening.ProfitAndLoss.label.autoCalc')}
                  </Button>
                  <HelpIcon>
                    {t('features.InvestmentScreening.ProfitAndLoss.help.autoCalcDesc1')}<br />
                    {projectData?.equipmentUsageType === EquipmentUsageType.GeneralPurpose.code
                      ? t('features.InvestmentScreening.ProfitAndLoss.help.autoCalcDesc2Generic')
                      : t('features.InvestmentScreening.ProfitAndLoss.help.autoCalcDesc2Sales')}<br /><br />
                    {t('features.InvestmentScreening.ProfitAndLoss.help.autoCalcDesc3')}
                  </HelpIcon>
                </Flex>
              </HStack>
              <TableContainer overflowX="auto" overflowY="auto" maxH="60vh" mt="20px" mb="20px" maxW="calc(96vw - 250px - 70px)">
                <Table variant="simple" size="sm" width="auto" sx={{
                  tableLayout: "fixed",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  "& td, & th": {
                    borderBottom: "1px solid",
                    borderBottomColor: "gray.200",
                    minHeight: "40px"
                  }
                }}>
                  <colgroup>
                    <col style={{ minWidth: '260px', width: '260px' }} />
                    <col style={{ minWidth: '120px', width: '120px' }} />
                    {fiscalMonthsString.map((_, idx) => (
                      <col key={idx} style={{ minWidth: '150px', width: '150px' }} />
                    ))}
                  </colgroup>
                  <Thead bg="gray.100">
                    <Tr>
                      <Th
                        p={2}
                        position="sticky"
                        top={0}
                        left={0}
                        bg="gray.100"
                        zIndex={2}
                        borderLeft="1px solid"
                        borderTop="1px solid"
                        borderRight="1px solid"
                        borderColor="gray.200"
                        whiteSpace="normal"
                        wordBreak="break-word"
                      >
                        {t('features.InvestmentScreening.ProfitAndLoss.label.itemHeader')}
                      </Th>
                      <Th p={2} textAlign="center" whiteSpace="normal" wordBreak="break-word" borderTop="1px solid" borderRight="1px solid" borderColor="gray.200" position="sticky" top={0} left="260px" bg="gray.100" zIndex={2} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)">
                        {t('features.InvestmentScreening.ProfitAndLoss.label.currencyHeader')}
                      </Th>
                      {fiscalMonthsString.map((month, idx) => (
                        <Th
                          key={idx}
                          position="sticky"
                          top={0}
                          zIndex={1}
                          p={2}
                          textAlign="center"
                          borderTop="1px solid"
                          borderRight="1px solid"
                          borderColor="gray.200"
                          bg="gray.100"
                        >
                          {month}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {/* ===== 1. 売上合計 (表示専用) ===== */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="bold">
                        <Text pl={4}>{t('features.InvestmentScreening.ProfitAndLoss.row.totalSales')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)"></Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" textAlign="right" >
                          -
                        </Td>
                      ))}
                    </Tr>
                    {/* 1-1. 製品売上 (表示専用) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Text pl={8}>{t('features.InvestmentScreening.ProfitAndLoss.row.productSales')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)"></Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" textAlign="right" >
                          -
                        </Td>
                      ))}
                    </Tr>
                    {/* 1-2. PJ単位金型・設備費回収 (表示専用) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Text pl={8}>{t('features.InvestmentScreening.ProfitAndLoss.row.moldEquipmentRecovery')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)"></Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" textAlign="right" >
                          -
                        </Td>
                      ))}
                    </Tr>
                    {/* 1-3. PJ単位開発費回収 (表示専用) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Text pl={8}>{t('features.InvestmentScreening.ProfitAndLoss.row.devRecovery')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)"></Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" textAlign="right" >
                          -
                        </Td>
                      ))}
                    </Tr>

                    {/* ===== 2. 売上原価 (表示専用) ===== */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="bold">
                        <Text pl={4}>{t('features.InvestmentScreening.ProfitAndLoss.row.salesCost')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)"></Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" textAlign="right" >
                          -
                        </Td>
                      ))}
                    </Tr>
                    {/* 2-1. 材料費 (表示専用) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Text pl={8}>{t('features.InvestmentScreening.ProfitAndLoss.row.materialCost')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)"></Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" textAlign="right" >
                          -
                        </Td>
                      ))}
                    </Tr>
                    {/* 2-2. 外注加工費 (入力) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Text pl={8}>{t('features.InvestmentScreening.ProfitAndLoss.row.outsourcingCost')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)">
                        <FormControl isInvalid={errors.expenseCurrency.isError && !outsourcingProcessingCostCurrencyId}>
                          <Select
                            size="sm"
                            w="90px"
                            mx="auto"
                            value={outsourcingProcessingCostCurrencyId}
                            onChange={(e) => setOutsourcingProcessingCostCurrencyId(e.target.value)}
                            isDisabled={inputMode === "google"}
                          >
                            <option value="">{t('features.InvestmentScreening.ProfitAndLoss.label.select')}</option>
                            {currencyData.map((currency) => (
                              <option key={currency.currencyId} value={currency.currencyId}>
                                {currency.currencyName}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </Td>
                      {monthlyExpenseData.map((data, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" >
                          <CustomNumberInput
                            value={data.outsourcingProcessingCost}
                            onChange={(v) => handleMonthlyExpenseChange(idx, 'outsourcingProcessingCost', v || 0)}
                            max={Number.MAX_VALUE}
                            decimalPlaces={2}
                            isDisabled={isAfterEOL(idx) || inputMode === "google"}
                            size="sm"
                          />
                        </Td>
                      ))}
                    </Tr>
                    {/* 2-3. 労務費 (表示専用) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Text pl={8}>{t('features.InvestmentScreening.ProfitAndLoss.row.laborCost')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)"></Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" textAlign="right" >
                          -
                        </Td>
                      ))}
                    </Tr>
                    {/* 2-3-1. 生産工場労務費 (入力) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Text pl={12}>{t('features.InvestmentScreening.ProfitAndLoss.row.productionLaborCost')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)">
                        <FormControl isInvalid={errors.expenseCurrency.isError && !productionLaborCostCurrencyId}>
                          <Select
                            size="sm"
                            w="90px"
                            mx="auto"
                            value={productionLaborCostCurrencyId}
                            onChange={(e) => setProductionLaborCostCurrencyId(e.target.value)}
                            isDisabled={inputMode === "google"}
                          >
                            <option value="">{t('features.InvestmentScreening.ProfitAndLoss.label.select')}</option>
                            {currencyData.map((currency) => (
                              <option key={currency.currencyId} value={currency.currencyId}>
                                {currency.currencyName}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </Td>
                      {monthlyExpenseData.map((data, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" >
                          <CustomNumberInput
                            value={data.productionLaborCost}
                            onChange={(v) => handleMonthlyExpenseChange(idx, 'productionLaborCost', v || 0)}
                            max={Number.MAX_VALUE}
                            decimalPlaces={2}
                            isDisabled={isAfterEOL(idx) || inputMode === "google"}
                            size="sm"
                          />
                        </Td>
                      ))}
                    </Tr>
                    {/* 2-3-2. 国内工場労務費 (入力) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Text pl={12}>{t('features.InvestmentScreening.ProfitAndLoss.row.domesticLaborCost')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)">
                        <FormControl isInvalid={errors.expenseCurrency.isError && !domesticLaborCostCurrencyId}>
                          <Select
                            size="sm"
                            w="90px"
                            mx="auto"
                            value={domesticLaborCostCurrencyId}
                            onChange={(e) => setDomesticLaborCostCurrencyId(e.target.value)}
                            isDisabled={inputMode === "google"}
                          >
                            <option value="">{t('features.InvestmentScreening.ProfitAndLoss.label.select')}</option>
                            {currencyData.map((currency) => (
                              <option key={currency.currencyId} value={currency.currencyId}>
                                {currency.currencyName}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </Td>
                      {monthlyExpenseData.map((data, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" >
                          <CustomNumberInput
                            value={data.domesticLaborCost}
                            onChange={(v) => handleMonthlyExpenseChange(idx, 'domesticLaborCost', v || 0)}
                            max={Number.MAX_VALUE}
                            decimalPlaces={2}
                            isDisabled={isAfterEOL(idx) || inputMode === "google"}
                            size="sm"
                          />
                        </Td>
                      ))}
                    </Tr>
                    {/* 2-4. 工場経費 (表示専用) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Text pl={8}>{t('features.InvestmentScreening.ProfitAndLoss.row.factoryExpenses')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)"></Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" textAlign="right" >
                          -
                        </Td>
                      ))}
                    </Tr>
                    {/* 2-4-1. 生産工場経費 (表示専用) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Text pl={12}>{t('features.InvestmentScreening.ProfitAndLoss.row.productionFactoryExpenses')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)"></Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" textAlign="right" >
                          -
                        </Td>
                      ))}
                    </Tr>
                    {/* 2-4-1-1. 償却費以外の工場経費（生産） (入力) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Text pl={16}>{t('features.InvestmentScreening.ProfitAndLoss.row.overHeadCost')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)">
                        <FormControl isInvalid={errors.expenseCurrency.isError && !overseasFactoryExpensesCurrencyId}>
                          <Select
                            size="sm"
                            w="90px"
                            mx="auto"
                            value={overseasFactoryExpensesCurrencyId}
                            onChange={(e) => setOverseasFactoryExpensesCurrencyId(e.target.value)}
                            isDisabled={inputMode === "google"}
                          >
                            <option value="">{t('features.InvestmentScreening.ProfitAndLoss.label.select')}</option>
                            {currencyData.map((currency) => (
                              <option key={currency.currencyId} value={currency.currencyId}>
                                {currency.currencyName}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </Td>
                      {monthlyExpenseData.map((data, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" >
                          <CustomNumberInput
                            value={data.overseasFactoryExpenses}
                            onChange={(v) => handleMonthlyExpenseChange(idx, 'overseasFactoryExpenses', v || 0)}
                            max={Number.MAX_VALUE}
                            decimalPlaces={2}
                            isDisabled={isAfterEOL(idx) || inputMode === "google"}
                            size="sm"
                          />
                        </Td>
                      ))}
                    </Tr>
                    {/* 2-4-1-2. 減価償却費（生産） (表示専用) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Flex align="center" gap={1}>
                          <Text pl={16}>{t('features.InvestmentScreening.ProfitAndLoss.row.depreciation')}</Text>
                          <HelpIcon size="16px"> 
                            {t('features.InvestmentScreening.ProfitAndLoss.help.productionLaborCostDesc.depreciationDesc1')}<br/>
                            {t('features.InvestmentScreening.ProfitAndLoss.help.productionLaborCostDesc.depreciationDesc2')}<br/>
                            {t('features.InvestmentScreening.ProfitAndLoss.help.productionLaborCostDesc.depreciationDesc3')}
                          </HelpIcon>
                        </Flex>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)"></Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" textAlign="right" >
                          -
                        </Td>
                      ))}
                    </Tr>
                    {/* 2-4-1-2-1. 投資対象設備（生産） (表示専用: 設備画面から自動計算) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Flex align="center" gap={1}>
                          <Text pl={20}>{t('features.InvestmentScreening.ProfitAndLoss.row.investmentTargetEquipment')}</Text>
                          <HelpIcon size="16px"> 
                            {t('features.InvestmentScreening.ProfitAndLoss.help.productionLaborCostDesc.investmentTargetEquipmentDesc1')}
                          </HelpIcon>
                        </Flex>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)"></Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" textAlign="right" >
                          -
                        </Td>
                      ))}
                    </Tr>
                    {/* 2-4-1-2-2. 工場建物（生産） (入力) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Flex align="center" gap={1}>
                          <Text pl={20}>{t('features.InvestmentScreening.ProfitAndLoss.row.factoryBuilding')}</Text>
                          <HelpIcon size="16px"> 
                            {t('features.InvestmentScreening.ProfitAndLoss.help.factoryBuildingDesc')}
                          </HelpIcon>
                        </Flex>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)">
                        <FormControl isInvalid={errors.expenseCurrency.isError && !factoryBuildingDepreciationCurrencyId}>
                          <Select
                            size="sm"
                            w="90px"
                            mx="auto"
                            value={factoryBuildingDepreciationCurrencyId}
                            onChange={(e) => setFactoryBuildingDepreciationCurrencyId(e.target.value)}
                            isDisabled={inputMode === "google"}
                          >
                            <option value="">{t('features.InvestmentScreening.ProfitAndLoss.label.select')}</option>
                            {currencyData.map((currency) => (
                              <option key={currency.currencyId} value={currency.currencyId}>
                                {currency.currencyName}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </Td>
                      {monthlyExpenseData.map((data, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" >
                          <CustomNumberInput
                            value={data.factoryBuildingDepreciation}
                            onChange={(v) => handleMonthlyExpenseChange(idx, 'factoryBuildingDepreciation', v || 0)}
                            max={Number.MAX_VALUE}
                            decimalPlaces={2}
                            isDisabled={isAfterEOL(idx) || inputMode === "google"}
                            size="sm"
                          />
                        </Td>
                      ))}
                    </Tr>
                    {/* 2-4-1-2-2.5. 過去PJの投資対象設備（生産） - 条件付き表示 */}
                    {projectData?.pastProjectId && (
                      <Tr>
                        <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                          <Flex align="center">
                            <Text pl={20} mr="5px">{t('features.InvestmentScreening.ProfitAndLoss.row.pastInvestmentTargetEquipment')}</Text>
                            <HelpIcon size="16px">
                              {t('features.InvestmentScreening.ProfitAndLoss.help.pastEquipmentDesc1')}<br />
                              {t('features.InvestmentScreening.ProfitAndLoss.help.pastEquipmentDesc2')}<br />
                              {t('features.InvestmentScreening.ProfitAndLoss.help.pastEquipmentDesc3')}
                            </HelpIcon>
                          </Flex>
                        </Th>
                        <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)">
                          <FormControl isInvalid={errors.expenseCurrency.isError && !pastEquipmentCostCurrencyId}>
                            <Select
                              size="sm"
                              w="90px"
                              mx="auto"
                              value={pastEquipmentCostCurrencyId}
                              onChange={(e) => setPastEquipmentCostCurrencyId(e.target.value)}
                              isDisabled={inputMode === "google"}
                            >
                              <option value="">{t('features.InvestmentScreening.ProfitAndLoss.label.select')}</option>
                              {currencyData.map((currency) => (
                                <option key={currency.currencyId} value={currency.currencyId}>
                                  {currency.currencyName}
                                </option>
                              ))}
                            </Select>
                          </FormControl>
                        </Td>
                        {fiscalMonthsString.map((month, idx) => (
                          <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" >
                            <CustomNumberInput
                              value={pastEquipmentCost[idx]?.value ?? 0}
                              onChange={(value) => {
                                handleChangeArrayValue(setPastEquipmentCost, idx, {
                                  year: pastEquipmentCost[idx]?.year,
                                  month: pastEquipmentCost[idx]?.month,
                                  value: value || 0
                                })
                              }}
                              max={Number.MAX_VALUE}
                              decimalPlaces={2}
                              isDisabled={isAfterEOL(idx) || inputMode === "google"}
                              size="sm"
                            />
                          </Td>
                        ))}
                      </Tr>
                    )}
                    {/* 2-4-1-2-3. その他の設備（生産） (入力) - otherEquipmentCostを使用（損益試算の減価償却費計算に連携） */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Flex align="center">
                          <Text pl={20} mr="5px">{t('features.InvestmentScreening.ProfitAndLoss.row.otherEquipment')}</Text>
                          <HelpIcon size="16px">
                            {t('features.InvestmentScreening.ProfitAndLoss.help.otherEquipmentDesc1')}<br />
                            {t('features.InvestmentScreening.ProfitAndLoss.help.otherEquipmentDesc2')}
                          </HelpIcon>
                        </Flex>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)">
                        <FormControl isInvalid={errors.expenseCurrency.isError && !otherEquipmentCostCurrencyId}>
                          <Select
                            size="sm"
                            w="90px"
                            mx="auto"
                            value={otherEquipmentCostCurrencyId}
                            onChange={(e) => setOtherEquipmentCostCurrencyId(e.target.value)}
                            isDisabled={inputMode === "google"}
                          >
                            <option value="">{t('features.InvestmentScreening.ProfitAndLoss.label.select')}</option>
                            {currencyData.map((currency) => (
                              <option key={currency.currencyId} value={currency.currencyId}>
                                {currency.currencyName}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" >
                          <CustomNumberInput
                            value={otherEquipmentCost[idx]?.value ?? 0}
                            onChange={(value) => {
                              handleChangeArrayValue(setOtherEquipmentCost, idx, {
                                year: otherEquipmentCost[idx]?.year,
                                month: otherEquipmentCost[idx]?.month,
                                value: value || 0
                              })
                            }}
                            max={Number.MAX_VALUE}
                            decimalPlaces={2}
                            isDisabled={isAfterEOL(idx) || inputMode === "google"}
                            size="sm"
                          />
                        </Td>
                      ))}
                    </Tr>
                    {/* 2-4-2. 国内工場経費 (表示専用) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Flex align="center">
                          <Text pl={12} mr="5px">{t('features.InvestmentScreening.ProfitAndLoss.row.domesticFactoryExpenses')}</Text>
                          <HelpIcon size="16px">
                            {t('features.InvestmentScreening.ProfitAndLoss.help.domesticFactoryExpenses1')}
                          </HelpIcon>
                        </Flex>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)"></Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" textAlign="right" >
                          -
                        </Td>
                      ))}
                    </Tr>
                    {/* 2-4-2-1. 償却費以外の工場経費（国内） (入力) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Text pl={16}>{t('features.InvestmentScreening.ProfitAndLoss.row.overHeadCost')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)">
                        <FormControl isInvalid={errors.expenseCurrency.isError && !domesticFactoryExpensesCurrencyId}>
                          <Select
                            size="sm"
                            w="90px"
                            mx="auto"
                            value={domesticFactoryExpensesCurrencyId}
                            onChange={(e) => setDomesticFactoryExpensesCurrencyId(e.target.value)}
                            isDisabled={inputMode === "google"}
                          >
                            <option value="">{t('features.InvestmentScreening.ProfitAndLoss.label.select')}</option>
                            {currencyData.map((currency) => (
                              <option key={currency.currencyId} value={currency.currencyId}>
                                {currency.currencyName}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </Td>
                      {monthlyExpenseData.map((data, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" >
                          <CustomNumberInput
                            value={data.domesticFactoryExpenses}
                            onChange={(v) => handleMonthlyExpenseChange(idx, 'domesticFactoryExpenses', v || 0)}
                            max={Number.MAX_VALUE}
                            decimalPlaces={2}
                            isDisabled={isAfterEOL(idx) || inputMode === "google"}
                            size="sm"
                          />
                        </Td>
                      ))}
                    </Tr>
                    {/* 2-4-2-2. 減価償却費（国内） (表示専用) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Flex align="center" gap={1}>
                          <Text pl={16}>{t('features.InvestmentScreening.ProfitAndLoss.row.depreciation')}</Text>
                          <HelpIcon size="16px"> 
                            {t('features.InvestmentScreening.ProfitAndLoss.help.domesticLaborCostDesc.depreciationDesc1')}<br/>
                            {t('features.InvestmentScreening.ProfitAndLoss.help.domesticLaborCostDesc.depreciationDesc2')}<br/>
                            {t('features.InvestmentScreening.ProfitAndLoss.help.domesticLaborCostDesc.depreciationDesc3')}
                          </HelpIcon>
                        </Flex>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)"></Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" textAlign="right" >
                          -
                        </Td>
                      ))}
                    </Tr>
                    {/* 2-4-2-2-1. 投資対象設備（国内） (入力) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Flex align="center" gap={1}>
                          <Text pl={20}>{t('features.InvestmentScreening.ProfitAndLoss.row.investmentTargetEquipment')}</Text>
                          <HelpIcon size="16px"> 
                            {t('features.InvestmentScreening.ProfitAndLoss.help.domesticLaborCostDesc.investmentTargetEquipmentDesc1')}<br/>
                            {t('features.InvestmentScreening.ProfitAndLoss.help.domesticLaborCostDesc.investmentTargetEquipmentDesc2')}
                          </HelpIcon>
                        </Flex>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)">
                        <FormControl isInvalid={errors.expenseCurrency.isError && !domesticInvestmentDepreciationCurrencyId}>
                          <Select
                            size="sm"
                            w="90px"
                            mx="auto"
                            value={domesticInvestmentDepreciationCurrencyId}
                            onChange={(e) => setDomesticInvestmentDepreciationCurrencyId(e.target.value)}
                            isDisabled={inputMode === "google"}
                          >
                            <option value="">{t('features.InvestmentScreening.ProfitAndLoss.label.select')}</option>
                            {currencyData.map((currency) => (
                              <option key={currency.currencyId} value={currency.currencyId}>
                                {currency.currencyName}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </Td>
                      {monthlyExpenseData.map((data, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" >
                          <CustomNumberInput
                            value={data.domesticInvestmentDepreciation ?? 0}
                            onChange={(v) => handleMonthlyExpenseChange(idx, 'domesticInvestmentDepreciation', v || 0)}
                            max={Number.MAX_VALUE}
                            decimalPlaces={2}
                            isDisabled={isAfterEOL(idx) || inputMode === "google"}
                            size="sm"
                          />
                        </Td>
                      ))}
                    </Tr>
                    {/* 2-4-2-2-2. 工場建物（国内） (入力) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Flex align="center" gap={1}>
                          <Text pl={20}>{t('features.InvestmentScreening.ProfitAndLoss.row.factoryBuilding')}</Text>
                          <HelpIcon size="16px"> 
                            {t('features.InvestmentScreening.ProfitAndLoss.help.factoryBuildingDesc')}
                          </HelpIcon>
                        </Flex>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)">
                        <FormControl isInvalid={errors.expenseCurrency.isError && !domesticFactoryBuildingDepreciationCurrencyId}>
                          <Select
                            size="sm"
                            w="90px"
                            mx="auto"
                            value={domesticFactoryBuildingDepreciationCurrencyId}
                            onChange={(e) => setDomesticFactoryBuildingDepreciationCurrencyId(e.target.value)}
                            isDisabled={inputMode === "google"}
                          >
                            <option value="">{t('features.InvestmentScreening.ProfitAndLoss.label.select')}</option>
                            {currencyData.map((currency) => (
                              <option key={currency.currencyId} value={currency.currencyId}>
                                {currency.currencyName}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </Td>
                      {monthlyExpenseData.map((data, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" >
                          <CustomNumberInput
                            value={data.domesticFactoryBuildingDepreciation ?? 0}
                            onChange={(v) => handleMonthlyExpenseChange(idx, 'domesticFactoryBuildingDepreciation', v || 0)}
                            max={Number.MAX_VALUE}
                            decimalPlaces={2}
                            isDisabled={isAfterEOL(idx) || inputMode === "google"}
                            size="sm"
                          />
                        </Td>
                      ))}
                    </Tr>
                    {/* 2-4-2-2-3. その他の設備（国内） (入力) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Flex align="center">
                          <Text pl={20} mr="5px">{t('features.InvestmentScreening.ProfitAndLoss.row.otherEquipment')}</Text>
                          <HelpIcon size="16px">
                            {t('features.InvestmentScreening.ProfitAndLoss.help.otherEquipmentDesc1')}<br />
                            {t('features.InvestmentScreening.ProfitAndLoss.help.otherEquipmentDesc2')}
                          </HelpIcon>
                        </Flex>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)">
                        <FormControl isInvalid={errors.expenseCurrency.isError && !domesticOtherDepreciationCurrencyId}>
                          <Select
                            size="sm"
                            w="90px"
                            mx="auto"
                            value={domesticOtherDepreciationCurrencyId}
                            onChange={(e) => setDomesticOtherDepreciationCurrencyId(e.target.value)}
                            isDisabled={inputMode === "google"}
                          >
                            <option value="">{t('features.InvestmentScreening.ProfitAndLoss.label.select')}</option>
                            {currencyData.map((currency) => (
                              <option key={currency.currencyId} value={currency.currencyId}>
                                {currency.currencyName}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </Td>
                      {monthlyExpenseData.map((data, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" >
                          <CustomNumberInput
                            value={data.domesticOtherDepreciation ?? 0}
                            onChange={(v) => handleMonthlyExpenseChange(idx, 'domesticOtherDepreciation', v || 0)}
                            max={Number.MAX_VALUE}
                            decimalPlaces={2}
                            isDisabled={isAfterEOL(idx) || inputMode === "google"}
                            size="sm"
                          />
                        </Td>
                      ))}
                    </Tr>
                    {/* 2-5. 仕損費 (表示専用) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Text pl={8}>{t('features.InvestmentScreening.ProfitAndLoss.row.scrapCost')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)"></Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" textAlign="right" >
                          -
                        </Td>
                      ))}
                    </Tr>

                    {/* ===== 3. 販売費及び一般管理費 (表示専用) ===== */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="bold">
                        <Text pl={4}>{t('features.InvestmentScreening.ProfitAndLoss.row.sgaExpenses')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)"></Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" textAlign="right" >
                          -
                        </Td>
                      ))}
                    </Tr>
                    {/* 3-1. 生産工場販管費 (入力) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Text pl={8}>{t('features.InvestmentScreening.ProfitAndLoss.row.productionSgaExpenses')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)">
                        <FormControl isInvalid={errors.expenseCurrency.isError && !productionSgaExpensesCurrencyId}>
                          <Select
                            size="sm"
                            w="90px"
                            mx="auto"
                            value={productionSgaExpensesCurrencyId}
                            onChange={(e) => setProductionSgaExpensesCurrencyId(e.target.value)}
                            isDisabled={inputMode === "google"}
                          >
                            <option value="">{t('features.InvestmentScreening.ProfitAndLoss.label.select')}</option>
                            {currencyData.map((currency) => (
                              <option key={currency.currencyId} value={currency.currencyId}>
                                {currency.currencyName}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </Td>
                      {monthlyExpenseData.map((data, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" >
                          <CustomNumberInput
                            value={data.productionSgaExpenses}
                            onChange={(v) => handleMonthlyExpenseChange(idx, 'productionSgaExpenses', v || 0)}
                            max={Number.MAX_VALUE}
                            decimalPlaces={2}
                            isDisabled={isAfterEOL(idx) || inputMode === "google"}
                            size="sm"
                          />
                        </Td>
                      ))}
                    </Tr>
                    {/* 3-2. 国内工場販管費 (入力) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Flex align="center">
                          <Text pl={8} mr="5px">{t('features.InvestmentScreening.ProfitAndLoss.row.domesticSgaExpenses')}</Text>
                          <HelpIcon size="16px">
                            {t('features.InvestmentScreening.ProfitAndLoss.help.domesticSgaDesc1')}<br />
                            {t('features.InvestmentScreening.ProfitAndLoss.help.domesticSgaDesc2')}
                          </HelpIcon>
                        </Flex>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)">
                        <FormControl isInvalid={errors.expenseCurrency.isError && !domesticSgaExpensesCurrencyId}>
                          <Select
                            size="sm"
                            w="90px"
                            mx="auto"
                            value={domesticSgaExpensesCurrencyId}
                            onChange={(e) => setDomesticSgaExpensesCurrencyId(e.target.value)}
                            isDisabled={inputMode === "google"}
                          >
                            <option value="">{t('features.InvestmentScreening.ProfitAndLoss.label.select')}</option>
                            {currencyData.map((currency) => (
                              <option key={currency.currencyId} value={currency.currencyId}>
                                {currency.currencyName}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </Td>
                      {monthlyExpenseData.map((data, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" >
                          <CustomNumberInput
                            value={data.domesticSgaExpenses}
                            onChange={(v) => handleMonthlyExpenseChange(idx, 'domesticSgaExpenses', v || 0)}
                            max={Number.MAX_VALUE}
                            decimalPlaces={2}
                            isDisabled={isAfterEOL(idx) || inputMode === "google"}
                            size="sm"
                          />
                        </Td>
                      ))}
                    </Tr>
                    {/* 3-3. 販社販管費 (入力) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Text pl={8}>{t('features.InvestmentScreening.ProfitAndLoss.row.salesCompanySga')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)">
                        <FormControl isInvalid={errors.expenseCurrency.isError && !salesCompanySgaCurrencyId}>
                          <Select
                            size="sm"
                            w="90px"
                            mx="auto"
                            value={salesCompanySgaCurrencyId}
                            onChange={(e) => setSalesCompanySgaCurrencyId(e.target.value)}
                            isDisabled={inputMode === "google"}
                          >
                            <option value="">{t('features.InvestmentScreening.ProfitAndLoss.label.select')}</option>
                            {currencyData.map((currency) => (
                              <option key={currency.currencyId} value={currency.currencyId}>
                                {currency.currencyName}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </Td>
                      {monthlyExpenseData.map((data, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" >
                          <CustomNumberInput
                            value={data.salesCompanySga}
                            onChange={(v) => handleMonthlyExpenseChange(idx, 'salesCompanySga', v || 0)}
                            max={Number.MAX_VALUE}
                            decimalPlaces={2}
                            isDisabled={isAfterEOL(idx) || inputMode === "google"}
                            size="sm"
                          />
                        </Td>
                      ))}
                    </Tr>
                    {/* 3-4. 開発費 (入力) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Flex align="center">
                          <Text pl={8} mr="5px">{t('features.InvestmentScreening.ProfitAndLoss.row.devCost')}</Text>
                          <HelpIcon size="16px">
                            {t('features.InvestmentScreening.ProfitAndLoss.help.devCostDesc1')}<br />
                            {t('features.InvestmentScreening.ProfitAndLoss.help.devCostDesc2')}<br />
                            {t('features.InvestmentScreening.ProfitAndLoss.help.devCostDesc3')}<br />
                            {t('features.InvestmentScreening.ProfitAndLoss.help.devCostDesc4')}
                          </HelpIcon>
                        </Flex>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)">
                        <FormControl isInvalid={errors.expenseCurrency.isError && !devCostCurrencyId}>
                          <Select
                            size="sm"
                            w="90px"
                            mx="auto"
                            value={devCostCurrencyId}
                            onChange={(e) => setDevCostCurrencyId(e.target.value)}
                            isDisabled={inputMode === "google"}
                          >
                            <option value="">{t('features.InvestmentScreening.ProfitAndLoss.label.select')}</option>
                            {currencyData.map((currency) => (
                              <option key={currency.currencyId} value={currency.currencyId}>
                                {currency.currencyName}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" >
                          <CustomNumberInput
                            value={devCost[idx]?.value ?? 0}
                            onChange={(value) => {
                              handleChangeArrayValue(setDevCost, idx, {
                                year: devCost[idx].year,
                                month: devCost[idx].month,
                                value: value || 0
                              })
                            }}
                            max={Number.MAX_VALUE}
                            decimalPlaces={2}
                            isDisabled={isAfterEOL(idx) || inputMode === "google"}
                            size="sm"
                          />
                        </Td>
                      ))}
                    </Tr>
                    {/* 3-5. ロイヤリティ (入力) */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="normal">
                        <Text pl={8}>{t('features.InvestmentScreening.ProfitAndLoss.row.royalty')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)">
                        <FormControl isInvalid={errors.expenseCurrency.isError && !royaltyCurrencyId}>
                          <Select
                            size="sm"
                            w="90px"
                            mx="auto"
                            value={royaltyCurrencyId}
                            onChange={(e) => setRoyaltyCurrencyId(e.target.value)}
                            isDisabled={inputMode === "google"}
                          >
                            <option value="">{t('features.InvestmentScreening.ProfitAndLoss.label.select')}</option>
                            {currencyData.map((currency) => (
                              <option key={currency.currencyId} value={currency.currencyId}>
                                {currency.currencyName}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </Td>
                      {monthlyExpenseData.map((data, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" >
                          <CustomNumberInput
                            value={data.royalty}
                            onChange={(v) => handleMonthlyExpenseChange(idx, 'royalty', v || 0)}
                            max={Number.MAX_VALUE}
                            decimalPlaces={2}
                            isDisabled={isAfterEOL(idx) || inputMode === "google"}
                            size="sm"
                          />
                        </Td>
                      ))}
                    </Tr>

                    {/* ===== 4. その他の経費 (入力) ===== */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="bold">
                        <Flex align="center">
                          <Text pl={4} mr="5px">{t('features.InvestmentScreening.ProfitAndLoss.row.otherCost')}</Text>
                          <HelpIcon size="16px">
                            {t('features.InvestmentScreening.ProfitAndLoss.help.otherCostDesc1')}<br />
                            {t('features.InvestmentScreening.ProfitAndLoss.help.otherCostDesc2')}
                          </HelpIcon>
                        </Flex>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)">
                        <FormControl isInvalid={errors.expenseCurrency.isError && !otherCostCurrencyId}>
                          <Select
                            size="sm"
                            w="90px"
                            mx="auto"
                            value={otherCostCurrencyId}
                            onChange={(e) => setOtherCostCurrencyId(e.target.value)}
                            isDisabled={inputMode === "google"}
                          >
                            <option value="">{t('features.InvestmentScreening.ProfitAndLoss.label.select')}</option>
                            {currencyData.map((currency) => (
                              <option key={currency.currencyId} value={currency.currencyId}>
                                {currency.currencyName}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" >
                          <CustomNumberInput
                            value={otherCost[idx]?.value ?? 0}
                            onChange={(value) => {
                              handleChangeArrayValue(setOtherCost, idx, {
                                year: otherCost[idx].year,
                                month: otherCost[idx].month,
                                value: value || 0
                              })
                            }}
                            max={Number.MAX_VALUE}
                            decimalPlaces={2}
                            isDisabled={isAfterEOL(idx) || inputMode === "google"}
                            size="sm"
                          />
                        </Td>
                      ))}
                    </Tr>

                    {/* ===== 5. 営業利益 (表示専用) ===== */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="bold">
                        <Text pl={4}>{t('features.InvestmentScreening.ProfitAndLoss.row.operatingProfit')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)"></Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" textAlign="right" >
                          -
                        </Td>
                      ))}
                    </Tr>

                    {/* ===== 6. 営業利益率 (表示専用) ===== */}
                    <Tr>
                      <Th p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" fontWeight="bold">
                        <Text pl={4}>{t('features.InvestmentScreening.ProfitAndLoss.row.operatingProfitRate')}</Text>
                      </Th>
                      <Td p={2} borderRight="1px solid" borderColor="gray.200" position="sticky" left="260px" bg="white" zIndex={1} boxShadow="4px 0 8px -4px rgba(0, 0, 0, 0.15)"></Td>
                      {fiscalMonthsString.map((month, idx) => (
                        <Td key={idx} p={2} borderRight="1px solid" borderColor="gray.200" textAlign="right" >
                          -
                        </Td>
                      ))}
                    </Tr>
                    {/* 通貨未選択エラーメッセージ行 */}
                    {errors.expenseCurrency.isError && (
                      <Tr>
                        <Td p={2} position="sticky" left={0} bg="white" zIndex={1} borderLeft="1px solid" borderRight="1px solid" borderColor="gray.200"></Td>
                        <Td p={2} colSpan={fiscalMonthsString.length + 1} borderRight="1px solid" borderColor="gray.200">
                          <Text color="red.500" fontSize="sm">
                            {errors.expenseCurrency.errorMessage}
                          </Text>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>

            {/* 複数ファイルの年度別集計結果を表示 */}
            {fiscalDataList.map((fileData, fileIndex) => (
              <Box key={fileIndex} mb={6}>
                <Text fontWeight="bold">
                  {t('features.InvestmentScreening.ProfitAndLoss.label.yearlyResultPrefix')}{dateRangeLabelList[fileIndex]?.label && `（${dateRangeLabelList[fileIndex].label}）`}
                </Text>
                <Text>
                  {fileData.plTitle && `${fileData.plTitle} `} unit:
                  {fileData.currency === CurrencyNameJPY
                    ? ` ${JP_CURRENCY_UNIT}${fileData.currency}`
                    : ` ${LC_CURRENCY_UNIT}${fileData.currency}`}
                </Text>
                <TableContainer overflowX="auto" overflowY="auto" maxH="40vh" mt="20px" mb="20px" maxW="calc(96vw - 250px - 70px)">
                  <Table
                    className="mm-ceas-table"
                    style={{ borderCollapse: "separate" }}
                    cellSpacing={0}
                    variant="simple"
                    borderColor="gray.200"
                    size="sm"
                    mt={2}
                    width="auto"
                    sx={{ tableLayout: 'auto' }}
                  >
                    <Thead>
                      <Tr>
                        <Th textAlign="center" position="sticky" top={0} bg="gray.100" zIndex={1} style={{ wordBreak: 'keep-all' }}>{t('features.InvestmentScreening.ProfitAndLoss.label.yearHeader')}</Th>
                        <Th textAlign="center" position="sticky" top={0} bg="gray.100" zIndex={1} style={{ wordBreak: 'keep-all' }}>{t('features.InvestmentScreening.ProfitAndLoss.label.salesHeader')}</Th>
                        <Th textAlign="center" position="sticky" top={0} bg="gray.100" zIndex={1} minW="90px" style={{ wordBreak: 'keep-all' }}>{t('features.InvestmentScreening.ProfitAndLoss.label.materialCostHeader')}</Th>
                        <Th textAlign="center" position="sticky" top={0} bg="gray.100" zIndex={1} minW="90px" style={{ wordBreak: 'keep-all' }}>{t('features.InvestmentScreening.ProfitAndLoss.label.outsourcingHeader')}</Th>
                        <Th textAlign="center" position="sticky" top={0} bg="gray.100" zIndex={1} minW="90px" style={{ wordBreak: 'keep-all' }}>{t('features.InvestmentScreening.ProfitAndLoss.label.laborCostHeader')}</Th>

                        <Th textAlign="center" position="sticky" top={0} bg="gray.100" zIndex={1} minW="100px" style={{ wordBreak: 'keep-all' }}>{t('features.InvestmentScreening.ProfitAndLoss.label.factorySgaHeader')}<br />{t('features.InvestmentScreening.ProfitAndLoss.label.devCostExclude')}</Th>

                        <Th textAlign="center" position="sticky" top={0} bg="gray.100" zIndex={1} minW="90px" style={{ wordBreak: 'keep-all' }}>{t('features.InvestmentScreening.ProfitAndLoss.label.overheadHeader')}</Th>
                        <Th textAlign="center" position="sticky" top={0} bg="gray.100" zIndex={1} minW="90px" style={{ wordBreak: 'keep-all' }}>{t('features.InvestmentScreening.ProfitAndLoss.label.depreciationHeader')}</Th>
                        <Th textAlign="center" position="sticky" top={0} bg="gray.100" zIndex={1} minW="100px" style={{ wordBreak: 'keep-all' }}>{t('features.InvestmentScreening.ProfitAndLoss.label.depreciationExclude')}<br />{t('features.InvestmentScreening.ProfitAndLoss.label.overheadHeader')}</Th>
                        <Th textAlign="center" position="sticky" top={0} bg="gray.100" zIndex={1} minW="90px" style={{ wordBreak: 'keep-all' }}>{t('features.InvestmentScreening.ProfitAndLoss.label.outsourcingRatioHeader')}<br />{t('features.InvestmentScreening.ProfitAndLoss.label.salesRatioSuffix')}</Th>
                        <Th textAlign="center" position="sticky" top={0} bg="gray.100" zIndex={1} minW="90px" style={{ wordBreak: 'keep-all' }}>{t('features.InvestmentScreening.ProfitAndLoss.label.laborCostRatioHeader')}<br />{t('features.InvestmentScreening.ProfitAndLoss.label.salesRatioSuffix')}</Th>

                        <Th textAlign="center" position="sticky" top={0} bg="gray.100" zIndex={1} minW="90px" style={{ wordBreak: 'keep-all' }}>{t('features.InvestmentScreening.ProfitAndLoss.label.factorySgaRatioHeader')}<br />{t('features.InvestmentScreening.ProfitAndLoss.label.salesRatioSuffix')}</Th>

                        <Th textAlign="center" position="sticky" top={0} bg="gray.100" zIndex={1} minW="90px" style={{ wordBreak: 'keep-all' }}>{t('features.InvestmentScreening.ProfitAndLoss.label.overheadRatioHeader')}<br />{t('features.InvestmentScreening.ProfitAndLoss.label.salesRatioSuffix')}</Th>
                        <Th textAlign="center" position="sticky" top={0} bg="gray.100" zIndex={1} minW="90px" style={{ wordBreak: 'keep-all' }}>{t('features.InvestmentScreening.ProfitAndLoss.label.depreciationRatioHeader')}<br />{t('features.InvestmentScreening.ProfitAndLoss.label.salesRatioSuffix')}</Th>
                        <Th textAlign="center" position="sticky" top={0} bg="gray.100" zIndex={1} minW="120px" style={{ wordBreak: 'keep-all' }}>{t('features.InvestmentScreening.ProfitAndLoss.label.depreciationExcludeOverheadRatioHeader')}<br />{t('features.InvestmentScreening.ProfitAndLoss.label.overheadHeader')}/<br />{t('features.InvestmentScreening.ProfitAndLoss.label.salesRatioSuffix')}</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {Object.keys(fileData.fiscalData).map((year) => {
                        const row = fileData.fiscalData[Number(year)];
                        const outsideRatio = row.OutSideToSalesRatio || 0;
                        const laborRatio = row.LaborToSalesRatio || 0;
                        const overheadRatio = row.OverheadToSalesRatio || 0;
                        const deprRatio = row.DeprToSalesRatio || 0;
                        const overheadExDeprRatio = row.OverheadExceptDeprToSalesRatio || 0;
                        const factorySgaRatio = row.FactorySgaToSalesRatio || 0;
                        return (
                          <Tr key={year}>
                            <Td textAlign="center">{year}</Td>
                            <Td textAlign="right">
                              {floorAndFormat(row.NetSales)}
                            </Td>
                            <Td textAlign="right">
                              {floorAndFormat(row.Material)}
                            </Td>
                            <Td textAlign="right">
                              {floorAndFormat(row.OutsideProcess)}
                            </Td>
                            <Td textAlign="right">
                              {floorAndFormat(row.Labor)}
                            </Td>

                            <Td textAlign="right">
                              {floorAndFormat(row.FactorySGA)}
                            </Td>

                            <Td textAlign="right">
                              {floorAndFormat(row.FactoryOverhead)}
                            </Td>
                            <Td textAlign="right">
                              {floorAndFormat(row.Depreciation)}
                            </Td>
                            <Td textAlign="right">
                              {floorAndFormat(row.FactoryOverheadExceptDepr)}
                            </Td>
                            <Td textAlign="right">
                              {(outsideRatio * 100).toFixed(1)}%
                            </Td>
                            <Td textAlign="right">
                              {(laborRatio * 100).toFixed(1)}%
                            </Td>

                            <Td textAlign="right">
                              {(factorySgaRatio * 100).toFixed(1)}%
                            </Td>

                            <Td textAlign="right">
                              {(overheadRatio * 100).toFixed(1)}%
                            </Td>
                            <Td textAlign="right">
                              {(deprRatio * 100).toFixed(1)}%
                            </Td>
                            <Td textAlign="right">
                              {(overheadExDeprRatio * 100).toFixed(1)}%
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
          </Box>
        </>)}
      </fieldset >

      <Flex justify="flex-end" mt="20px">
        <Box>
          <Button
            ml="10px"
            colorScheme="blue"
            name="companyDivision"
            onClick={handleDepartmentConfirm}
            isDisabled={!pagePermissions?.eabledCompanyDivision}
            ref={refs["companyDivision"]}
          >
            {t('features.InvestmentScreening.ProfitAndLoss.button.companyDivisionConfirm')}
          </Button>
          <ConfirmorList confirmorInfo={names["companyDivision"]} width={buttonWidths["companyDivision"]} />
        </Box>
        <Box>
          <Button
            ml="10px"
            colorScheme="blue"
            name="companyHeadDivision"
            onClick={handleDepartmentConfirm}
            isDisabled={!pagePermissions?.eabledCompanyHeadDivision}
            ref={refs["companyHeadDivision"]}
          >
            {t('features.InvestmentScreening.ProfitAndLoss.button.companyHeadDivisionConfirm')}
          </Button>
          <ConfirmorList confirmorInfo={names["companyHeadDivision"]} width={buttonWidths["companyHeadDivision"]} />
        </Box>
      </Flex>

      {/* 選択ボタンを押した後に確認するモーダル */}
      <Modal isOpen={isSpanConfirmOpen} onClose={onSpanConfirmClose} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="85vh">
          <ModalHeader>{t('features.InvestmentScreening.ProfitAndLoss.modal.registerConfirmTitle')}</ModalHeader>
          <ModalBody>
            {isSimpleMode ? (
              <Box>
                <Text>{t('common.message.simpleModeNoConfirmContent')}</Text>
              </Box>
            ) : (
              <Box mb={4}>
                <Text fontSize="md" mb={2}>
                  {t('features.InvestmentScreening.ProfitAndLoss.modal.registerConfirmMessage')}
                </Text>
                <Box p={2}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.ProfitAndLoss.label.productionFactoryLaborUnitPrice')}
                  </Text>
                  <Text>
                    {productionFactoryLaborUnitPrice ?? 0} {currencyData.find(c => c.currencyId === productionLaborCostCurrencyId)?.currencyName || ""} / min
                  </Text>
                </Box>
                <Box p={2}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.ProfitAndLoss.label.productionFactoryExpenseUnitPrice')}
                  </Text>
                  <Text>
                    {productionFactoryExpenseUnitPrice ?? 0} {currencyData.find(c => c.currencyId === overseasFactoryExpensesCurrencyId)?.currencyName || ""} / min
                  </Text>
                </Box>
                <Box p={2}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.ProfitAndLoss.label.baseLaborCostIncreaseRate')}
                  </Text>
                  <Text>
                    {projectData?.baseLaborCostIncreaseRate} %
                  </Text>
                </Box>
                {/* 売上原価 - 外注加工費 */}
                <Box p={2}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.ProfitAndLoss.row.outsourcingCost')}{t('features.InvestmentScreening.ProfitAndLoss.label.totalSuffix')}
                  </Text>
                  <Text>
                    {formatNumber(monthlyExpenseData.reduce((sum: Decimal, data: MonthlyExpenseData) => {
                      return sum.plus(data.outsourcingProcessingCost ?? 0);
                    }, new Decimal(0)), currencyData.find(c => c.currencyId === outsourcingProcessingCostCurrencyId)?.currencyName || "")} {currencyData.find(c => c.currencyId === outsourcingProcessingCostCurrencyId)?.currencyName || ""}
                  </Text>
                </Box>
                {/* 売上原価 - 労務費 */}
                <Box p={2}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.ProfitAndLoss.row.productionLaborCost')}{t('features.InvestmentScreening.ProfitAndLoss.label.totalSuffix')}
                  </Text>
                  <Text>
                    {formatNumber(monthlyExpenseData.reduce((sum: Decimal, data: MonthlyExpenseData) => {
                      return sum.plus(data.productionLaborCost ?? 0);
                    }, new Decimal(0)), currencyData.find(c => c.currencyId === productionLaborCostCurrencyId)?.currencyName || "")} {currencyData.find(c => c.currencyId === productionLaborCostCurrencyId)?.currencyName || ""}
                  </Text>
                </Box>
                <Box p={2}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.ProfitAndLoss.row.domesticLaborCost')}{t('features.InvestmentScreening.ProfitAndLoss.label.totalSuffix')}
                  </Text>
                  <Text>
                    {formatNumber(monthlyExpenseData.reduce((sum: Decimal, data: MonthlyExpenseData) => {
                      return sum.plus(data.domesticLaborCost ?? 0);
                    }, new Decimal(0)), currencyData.find(c => c.currencyId === domesticLaborCostCurrencyId)?.currencyName || "")} {currencyData.find(c => c.currencyId === domesticLaborCostCurrencyId)?.currencyName || ""}
                  </Text>
                </Box>
                {/* 売上原価 - 工場経費 - 生産工場経費 */}
                <Box p={2}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.ProfitAndLoss.row.overHeadCost')}{t('features.InvestmentScreening.ProfitAndLoss.label.productionSuffix')}{t('features.InvestmentScreening.ProfitAndLoss.label.totalSuffix')}
                  </Text>
                  <Text>
                    {formatNumber(monthlyExpenseData.reduce((sum: Decimal, data: MonthlyExpenseData) => {
                      return sum.plus(data.overseasFactoryExpenses ?? 0);
                    }, new Decimal(0)), currencyData.find(c => c.currencyId === overseasFactoryExpensesCurrencyId)?.currencyName || "")} {currencyData.find(c => c.currencyId === overseasFactoryExpensesCurrencyId)?.currencyName || ""}
                  </Text>
                </Box>
                <Box p={2}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.ProfitAndLoss.row.factoryBuilding')}{t('features.InvestmentScreening.ProfitAndLoss.label.productionSuffix')}{t('features.InvestmentScreening.ProfitAndLoss.label.totalSuffix')}
                  </Text>
                  <Text>
                    {formatNumber(monthlyExpenseData.reduce((sum: Decimal, data: MonthlyExpenseData) => {
                      return sum.plus(data.factoryBuildingDepreciation ?? 0);
                    }, new Decimal(0)), currencyData.find(c => c.currencyId === factoryBuildingDepreciationCurrencyId)?.currencyName || "")} {currencyData.find(c => c.currencyId === factoryBuildingDepreciationCurrencyId)?.currencyName || ""}
                  </Text>
                </Box>
                {projectData?.pastProjectId && (
                  <Box p={2}>
                    <Text fontWeight="bold">
                      {t('features.InvestmentScreening.ProfitAndLoss.row.pastInvestmentTargetEquipment')}{t('features.InvestmentScreening.ProfitAndLoss.label.productionSuffix')}{t('features.InvestmentScreening.ProfitAndLoss.label.totalSuffix')}
                    </Text>
                    <Text>
                      {formatNumber(pastEquipmentCost.reduce((sum: Decimal, c: MonthValue) => {
                        return sum.plus(c.value);
                      }, new Decimal(0)), currencyData.find(c => c.currencyId === pastEquipmentCostCurrencyId)?.currencyName || "")} {currencyData.find(c => c.currencyId === pastEquipmentCostCurrencyId)?.currencyName || ""}
                    </Text>
                  </Box>
                )}
                <Box p={2}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.ProfitAndLoss.row.otherEquipment')}{t('features.InvestmentScreening.ProfitAndLoss.label.productionSuffix')}{t('features.InvestmentScreening.ProfitAndLoss.label.totalSuffix')}
                  </Text>
                  <Text>
                    {formatNumber(otherEquipmentCost.reduce((sum: Decimal, c: MonthValue) => {
                      return sum.plus(c.value);
                    }, new Decimal(0)), currencyData.find(c => c.currencyId === otherEquipmentCostCurrencyId)?.currencyName || "")} {currencyData.find(c => c.currencyId === otherEquipmentCostCurrencyId)?.currencyName || ""}
                  </Text>
                </Box>
                {/* 売上原価 - 工場経費 - 国内工場経費 */}
                <Box p={2}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.ProfitAndLoss.row.overHeadCost')}{t('features.InvestmentScreening.ProfitAndLoss.label.domesticSuffix')}{t('features.InvestmentScreening.ProfitAndLoss.label.totalSuffix')}
                  </Text>
                  <Text>
                    {formatNumber(monthlyExpenseData.reduce((sum: Decimal, data: MonthlyExpenseData) => {
                      return sum.plus(data.domesticFactoryExpenses ?? 0);
                    }, new Decimal(0)), currencyData.find(c => c.currencyId === domesticFactoryExpensesCurrencyId)?.currencyName || "")} {currencyData.find(c => c.currencyId === domesticFactoryExpensesCurrencyId)?.currencyName || ""}
                  </Text>
                </Box>
                <Box p={2}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.ProfitAndLoss.row.investmentTargetEquipment')}{t('features.InvestmentScreening.ProfitAndLoss.label.domesticSuffix')}{t('features.InvestmentScreening.ProfitAndLoss.label.totalSuffix')}
                  </Text>
                  <Text>
                    {formatNumber(monthlyExpenseData.reduce((sum: Decimal, data: MonthlyExpenseData) => {
                      return sum.plus(data.domesticInvestmentDepreciation ?? 0);
                    }, new Decimal(0)), currencyData.find(c => c.currencyId === domesticInvestmentDepreciationCurrencyId)?.currencyName || "")} {currencyData.find(c => c.currencyId === domesticInvestmentDepreciationCurrencyId)?.currencyName || ""}
                  </Text>
                </Box>
                <Box p={2}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.ProfitAndLoss.row.factoryBuilding')}{t('features.InvestmentScreening.ProfitAndLoss.label.domesticSuffix')}{t('features.InvestmentScreening.ProfitAndLoss.label.totalSuffix')}
                  </Text>
                  <Text>
                    {formatNumber(monthlyExpenseData.reduce((sum: Decimal, data: MonthlyExpenseData) => {
                      return sum.plus(data.domesticFactoryBuildingDepreciation ?? 0);
                    }, new Decimal(0)), currencyData.find(c => c.currencyId === domesticFactoryBuildingDepreciationCurrencyId)?.currencyName || "")} {currencyData.find(c => c.currencyId === domesticFactoryBuildingDepreciationCurrencyId)?.currencyName || ""}
                  </Text>
                </Box>
                <Box p={2}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.ProfitAndLoss.row.otherEquipment')}{t('features.InvestmentScreening.ProfitAndLoss.label.domesticSuffix')}{t('features.InvestmentScreening.ProfitAndLoss.label.totalSuffix')}
                  </Text>
                  <Text>
                    {formatNumber(monthlyExpenseData.reduce((sum: Decimal, data: MonthlyExpenseData) => {
                      return sum.plus(data.domesticOtherDepreciation ?? 0);
                    }, new Decimal(0)), currencyData.find(c => c.currencyId === domesticOtherDepreciationCurrencyId)?.currencyName || "")} {currencyData.find(c => c.currencyId === domesticOtherDepreciationCurrencyId)?.currencyName || ""}
                  </Text>
                </Box>
                {/* 販売費及び一般管理費 */}
                <Box p={2}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.ProfitAndLoss.row.productionSgaExpenses')}{t('features.InvestmentScreening.ProfitAndLoss.label.totalSuffix')}
                  </Text>
                  <Text>
                    {formatNumber(monthlyExpenseData.reduce((sum: Decimal, data: MonthlyExpenseData) => {
                      return sum.plus(data.productionSgaExpenses ?? 0);
                    }, new Decimal(0)), currencyData.find(c => c.currencyId === productionSgaExpensesCurrencyId)?.currencyName || "")} {currencyData.find(c => c.currencyId === productionSgaExpensesCurrencyId)?.currencyName || ""}
                  </Text>
                </Box>
                <Box p={2}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.ProfitAndLoss.row.domesticSgaExpenses')}{t('features.InvestmentScreening.ProfitAndLoss.label.totalSuffix')}
                  </Text>
                  <Text>
                    {formatNumber(monthlyExpenseData.reduce((sum: Decimal, data: MonthlyExpenseData) => {
                      return sum.plus(data.domesticSgaExpenses ?? 0);
                    }, new Decimal(0)), currencyData.find(c => c.currencyId === domesticSgaExpensesCurrencyId)?.currencyName || "")} {currencyData.find(c => c.currencyId === domesticSgaExpensesCurrencyId)?.currencyName || ""}
                  </Text>
                </Box>
                <Box p={2}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.ProfitAndLoss.row.salesCompanySga')}{t('features.InvestmentScreening.ProfitAndLoss.label.totalSuffix')}
                  </Text>
                  <Text>
                    {formatNumber(monthlyExpenseData.reduce((sum: Decimal, data: MonthlyExpenseData) => {
                      return sum.plus(data.salesCompanySga ?? 0);
                    }, new Decimal(0)), currencyData.find(c => c.currencyId === salesCompanySgaCurrencyId)?.currencyName || "")} {currencyData.find(c => c.currencyId === salesCompanySgaCurrencyId)?.currencyName || ""}
                  </Text>
                </Box>
                <Box p={2}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.ProfitAndLoss.row.devCost')}{t('features.InvestmentScreening.ProfitAndLoss.label.totalSuffix')}
                  </Text>
                  <Text>
                    {formatNumber(devCost.reduce((sum: Decimal, c: MonthValue) => {
                      return sum.plus(c.value);
                    }, new Decimal(0)), currencyData.find(c => c.currencyId === devCostCurrencyId)?.currencyName || "")} {currencyData.find(c => c.currencyId === devCostCurrencyId)?.currencyName || ""}
                  </Text>
                </Box>
                <Box p={2}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.ProfitAndLoss.row.royalty')}{t('features.InvestmentScreening.ProfitAndLoss.label.totalSuffix')}
                  </Text>
                  <Text>
                    {formatNumber(monthlyExpenseData.reduce((sum: Decimal, data: MonthlyExpenseData) => {
                      return sum.plus(data.royalty ?? 0);
                    }, new Decimal(0)), currencyData.find(c => c.currencyId === royaltyCurrencyId)?.currencyName || "")} {currencyData.find(c => c.currencyId === royaltyCurrencyId)?.currencyName || ""}
                  </Text>
                </Box>
                <Box p={2}>
                  <Text fontWeight="bold">
                    {t('features.InvestmentScreening.ProfitAndLoss.row.otherCost')}{t('features.InvestmentScreening.ProfitAndLoss.label.totalSuffix')}
                  </Text>
                  <Text>
                    {formatNumber(otherCost.reduce((sum: Decimal, c: MonthValue) => {
                      return sum.plus(c.value);
                    }, new Decimal(0)), currencyData.find(c => c.currencyId === otherCostCurrencyId)?.currencyName || "")} {currencyData.find(c => c.currencyId === otherCostCurrencyId)?.currencyName || ""}
                  </Text>
                </Box>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={async (e) => {
              await saveProfitAndLoss(e);
              onSpanConfirmClose();
            }}>
              {t('features.InvestmentScreening.ProfitAndLoss.button.execute')}
            </Button>
            <Button variant="ghost" onClick={onSpanConfirmClose}>
              {t('features.InvestmentScreening.ProfitAndLoss.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 帳票ファイルアップロードモーダル */}
      <FileUploadModal
        isOpen={isOpenReportUploadModal}
        onClose={onCloseReportUploadModal}
        title={TITLE}
        projectId={projectId}
        folderName={"Report"}
        allowedExtensions={[]}
      />

      {/* PLSファイルアップロードモーダル */}
      <FileUploadModal
        isOpen={isOpenPLSUploadModal}
        onClose={onClosePLSUploadModal}
        title={TITLE}
        projectId={projectId}
        folderName={"ProfitAndLoss"}
        allowedExtensions={[]}
        onSaveComplete={fetchPLFileData}
      />
    </Box >
  );
});

export default ProfitAndLoss;
